import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import axios from 'axios'
import * as XLSX from 'xlsx'
import Swal from 'sweetalert2'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import {useLocation} from 'react-router-dom'
import {
  getExamResultQuestions,
  getExamResultStatistics,
  getExamResultStudents,
  getExamResultStudentDetails,
  evaluateStudentBookletCodes,
  applyStudentBookletEvaluation,
  updateExamAnswerKey,
  sendExamGradesToObs,
} from '../../pages/CustomController'
import {getOpticalQuestionReview} from '../../pages/exam-optical-reading/core/_requests'
import './ExamResultInput.css'

const API_URL = process.env.REACT_APP_API_URL
const SAVE_RESULTS = `${API_URL}/Custom/saveResults`

const STATUS_SEQUENCE = [
  'Matched',
  'Duplicate',
  'Unregistered',
  'BookletMismatch',
  'Ambiguous',
  'Skipped',
  'InvalidLine',
]

const STATUS_BADGE_CLASSES = {
  Matched: 'badge-light-success',
  Duplicate: 'badge-light-warning',
  Unregistered: 'badge-light-danger',
  BookletMismatch: 'badge-light-info',
  Ambiguous: 'badge-light-primary',
  Skipped: 'badge-light-secondary',
  InvalidLine: 'badge-light-dark',
}

const STATUS_LABELS = {
  Matched: 'Eşleşti',
  Duplicate: 'Mükerrer',
  Unregistered: 'Kayıtsız Öğrenci',
  BookletMismatch: 'Kitapçık Uyuşmuyor',
  Ambiguous: 'Birden Fazla Ders',
  Skipped: 'Yoksayıldı',
  InvalidLine: 'Geçersiz Satır',
}

const AI_COMMENT_PREVIEW_STYLE = {
  maxHeight: '18rem',
  overflowY: 'auto',
  fontSize: '0.875rem',
  lineHeight: 1.6,
}

const CRONBACH_ALPHA_TOOLTIP = (
  <div className='text-start'>
    <p className='mb-2'>
      Cronbach Alpha, bir testin sorularının aynı yapıyı ölçme derecesini gösteren iç tutarlılık katsayısıdır.
      Katsayı ne kadar yüksekse, maddelerin birbirini desteklediği ve testin güvenilirliğinin o kadar güçlü olduğu
      kabul edilir.
    </p>
    <p className='mb-2'>
      Değerler 0 ile 1 arasında değişir. 0'a yakın değerler soruların farklı yapıları ölçtüğünü, 1'e yaklaşan
      değerler ise soruların aynı yapıyı güçlü biçimde hedeflediğini gösterir.
    </p>
    <ul className='mb-2 ps-4'>
      <li>0,95 ve üzeri: Aşırı yüksek güvenilirlik – maddeler birbirine çok benzer olabilir.</li>
      <li>0,90 - 0,94 arası: Çok yüksek güvenilirlik – tutarlılık oldukça güçlüdür.</li>
      <li>0,80 - 0,89 arası: İyi güvenilirlik – test genel olarak tutarlıdır.</li>
      <li>0,70 - 0,79 arası: Kabul edilebilir güvenilirlik – standart uygulamalar için yeterlidir.</li>
      <li>0,60 - 0,69 arası: Sınırda güvenilirlik – maddeleri gözden geçirmek faydalı olabilir.</li>
      <li>0,40 - 0,59 arası: Düşük güvenilirlik – tutarlılık sorunları belirgindir.</li>
      <li>0,40'ın altı: Kabul edilemez – test güvenirliği yetersizdir.</li>
    </ul>
    <p className='mb-0'>
      Negatif değerlere düşen Cronbach Alpha, maddelerin ters puanlanmış olabileceğini; 0 değeri ise maddelerin ortak
      varyans üretmediğini gösterir.
    </p>
  </div>
)

const KR20_TOOLTIP = (
  <div className='text-start'>
    <p className='mb-2'>
      KR-20, doğru/yanlış şeklinde puanlanan maddelerden oluşan testlerin iç tutarlılığını ölçen güvenirlik katsayısıdır.
      Değer Cronbach Alpha ile aynı şekilde yorumlanır ve 1&#39;e yaklaştıkça testin güvenilirliğinin arttığını gösterir.
    </p>
    <p className='mb-0'>
      0&#39;a yakın değerler maddelerin tutarlılığının zayıf olduğunu, 1&#39;e yaklaşan değerler ise maddelerin aynı yapıyı güçlü
      biçimde hedeflediğini belirtir.
    </p>
  </div>
)

const isAiActive = process.env.REACT_APP_YZACTIVE === '1'

const parseNumericValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.')
    if (!normalized) {
      return null
    }

    const numeric = Number(normalized)
    if (Number.isFinite(numeric)) {
      return numeric
    }
  }

  return null
}

const baseDifficultyInsight = (value) => {
  const numeric = parseNumericValue(value)
  if (numeric === null) {
    return {label: 'Veri yok', needsReview: false, warn: false}
  }

  if (numeric < 0.2) {
    return {label: 'Çok zor madde ❗', needsReview: true, warn: true}
  }

  if (numeric < 0.4) {
    return {label: 'Zor madde', needsReview: false, warn: false}
  }

  if (numeric <= 0.59) {
    return {label: 'Orta güçlükte (ideal aralık ✅)', needsReview: false, warn: false}
  }

  if (numeric < 0.8) {
    return {label: 'Kolay madde', needsReview: false, warn: false}
  }

  return {label: 'Çok kolay madde ❗', needsReview: true, warn: true}
}

const baseDiscriminationInsight = (value) => {
  const numeric = parseNumericValue(value)
  if (numeric === null) {
    return {label: 'Veri yok', needsReview: false, warn: false}
  }

  if (numeric < 0) {
    return {label: 'Ters ayırt edici, ciddi problem ❌', needsReview: true, warn: true}
  }

  if (numeric < 0.2) {
    return {label: 'Zayıf ❗', needsReview: true, warn: true}
  }

  if (numeric < 0.3) {
    return {label: 'Kabul edilebilir', needsReview: false, warn: false}
  }

  if (numeric < 0.4) {
    return {label: 'İyi', needsReview: false, warn: false}
  }

  return {label: 'Çok iyi ayırt edicilik ✅', needsReview: false, warn: false}
}

const resolveDifficultyInsight = (analysis) => {
  const base = baseDifficultyInsight(analysis?.difficulty)
  const label =
    typeof analysis?.difficultyLabel === 'string' && analysis.difficultyLabel.trim().length > 0
      ? analysis.difficultyLabel
      : base.label
  const needsReview =
    typeof analysis?.difficultyNeedsReview === 'boolean' ? analysis.difficultyNeedsReview : base.needsReview
  const warn = needsReview || base.warn
  return {label, needsReview, warn}
}

const resolveDiscriminationInsight = (analysis) => {
  const base = baseDiscriminationInsight(analysis?.discrimination)
  const label =
    typeof analysis?.discriminationLabel === 'string' && analysis.discriminationLabel.trim().length > 0
      ? analysis.discriminationLabel
      : base.label
  const needsReview =
    typeof analysis?.discriminationNeedsReview === 'boolean'
      ? analysis.discriminationNeedsReview
      : base.needsReview
  const warn = needsReview || base.warn
  return {label, needsReview, warn}
}

const normalizeText = (value) => {
  if (typeof value !== 'string') {
    return ''
  }

  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/ı/g, 'i')
}

const labelMatchesAny = (label, keywords) => {
  const normalized = normalizeText(label)
  if (!normalized) {
    return false
  }
  return keywords
    .map((keyword) => normalizeText(keyword))
    .filter(Boolean)
    .some((keyword) => normalized.includes(keyword))
}

const shouldAutoReviewQuestion = (analysis) => {
  if (!analysis || typeof analysis.examQuestionId !== 'number') {
    return false
  }

  const difficultyValue = parseNumericValue(analysis?.difficulty)
  const discriminationValue = parseNumericValue(analysis?.discrimination)

  const difficultyInsight = resolveDifficultyInsight(analysis)
  const discriminationInsight = resolveDiscriminationInsight(analysis)

  const hasStrongNegativeDiscrimination =
    typeof discriminationValue === 'number' && discriminationValue < -0.2
  if (hasStrongNegativeDiscrimination) {
    return true
  }

  const difficultyNeedsReview =
    Boolean(difficultyInsight?.needsReview) ||
    labelMatchesAny(difficultyInsight?.label, ['çok zor']) ||
    (typeof difficultyValue === 'number' && difficultyValue < 0.2)

  if (!difficultyNeedsReview) {
    return false
  }

  const hasNegativeDiscrimination =
    typeof discriminationValue === 'number' && discriminationValue < 0

  const discriminationNeedsReview =
    Boolean(discriminationInsight?.needsReview) ||
    labelMatchesAny(discriminationInsight?.label, ['zayıf', 'zayif', 'ters', 'negatif']) ||
    (typeof discriminationValue === 'number' && discriminationValue < 0.2)

  return discriminationNeedsReview || hasNegativeDiscrimination
}

const buildAutoReviewData = ({examId, analysis, question, difficultyInsight, discriminationInsight}) => {
  if (!analysis || typeof analysis.examQuestionId !== 'number') {
    return null
  }

  const cacheKey = `${examId}-${analysis.examQuestionId}`

  const optionItems = Array.isArray(question?.questionItems) ? question.questionItems : []
  const normalizedOptions = optionItems
    .map((item, index) => {
      const label = resolveQuestionItemLabel(item, index)
      const text = sanitizeRichText(item?.name || '')
      const key =
        typeof item?.key === 'string' && item.key.trim().length > 0 ? item.key.trim() : null
      const optionId = typeof item?.id === 'number' ? item.id : null

      if (!label && !text) {
        return null
      }

      return {label, text, isCorrect: Boolean(item?.isTrue), key, id: optionId}
    })
    .filter(Boolean)

  const correctOptionLabels = normalizeAcceptedOptionsList(
    normalizedOptions
      .filter((option) => option.isCorrect)
      .map((option) => option.label || option.key || '')
  )

  const questionSegments = []
  const sharedTitle = sanitizeRichText(question?.sharedContentTitle || '')
  const sharedContent = sanitizeRichText(question?.sharedContentContent || '')
  const baseQuestionText = sanitizeRichText(question?.text || question?.title || '')

  if (sharedTitle) {
    questionSegments.push(sharedTitle)
  }
  if (sharedContent) {
    questionSegments.push(sharedContent)
  }
  if (baseQuestionText) {
    questionSegments.push(baseQuestionText)
  }

  const questionText = questionSegments.join('\n\n')

  const normalizedDifficulty = parseNumericValue(analysis?.difficulty)
  const normalizedDiscrimination = parseNumericValue(analysis?.discrimination)

  const payload = {
    examId,
    examQuestionId: analysis.examQuestionId,
    difficulty: typeof normalizedDifficulty === 'number' ? normalizedDifficulty : null,
    discrimination: typeof normalizedDiscrimination === 'number' ? normalizedDiscrimination : null,
    respondentCount:
      typeof analysis.respondentCount === 'number' ? analysis.respondentCount : undefined,
    correctCount: typeof analysis.correctCount === 'number' ? analysis.correctCount : undefined,
    blankCount: typeof analysis.blankCount === 'number' ? analysis.blankCount : undefined,
    difficultyLabel: difficultyInsight?.label || '',
    discriminationLabel: discriminationInsight?.label || '',
    questionText: questionText || null,
  }

  if (normalizedOptions.length > 0) {
    payload.options = normalizedOptions.map((option) => ({
      label: option.label,
      text: option.text,
      isCorrect: option.isCorrect,
      key: option.key,
      optionId: option.id,
    }))
  }

  if (correctOptionLabels.length > 0) {
    payload.correctOptionLabels = correctOptionLabels
  }

  const signaturePayload = {
    questionText,
    options: normalizedOptions.map((option) => ({
      id: option.id,
      key: option.key,
      label: option.label,
      text: option.text,
      isCorrect: option.isCorrect,
    })),
    correctOptionLabels,
  }

  const signature = [
    typeof normalizedDifficulty === 'number' ? normalizedDifficulty.toString() : 'null',
    typeof normalizedDiscrimination === 'number' ? normalizedDiscrimination.toString() : 'null',
    (typeof analysis.respondentCount === 'number' ? analysis.respondentCount : 0).toString(),
    (typeof analysis.correctCount === 'number' ? analysis.correctCount : 0).toString(),
    (typeof analysis.blankCount === 'number' ? analysis.blankCount : 0).toString(),
    difficultyInsight?.label || '',
    discriminationInsight?.label || '',
    JSON.stringify(signaturePayload),
  ].join('|')

  return {cacheKey, signature, payload, questionId: analysis.examQuestionId}
}

const getInsightClassName = (insight) => {
  if (insight.needsReview) {
    return 'text-danger fw-semibold'
  }

  if (insight.warn) {
    return 'text-warning fw-semibold'
  }

  return 'text-muted'
}

const getCronbachAlphaComment = (value, questionCount) => {
  if (value === null || value === undefined || Number.isNaN(value)) return null

  if (value < 0) return 'Negatif güvenilirlik: maddeler ters puanlanmış olabilir veya tutarlılık çok düşük.'
  if (value === 0) return 'Güvenilirlik yok (α=0): maddeler ortak varyans üretmiyor.'

  let base
  if (value >= 0.95) {
    base = 'Aşırı yüksek güvenilirlik (mükemmel); madde tekrarına veya aşırı benzerliğe işaret edebilir.'
  } else if (value >= 0.9) {
    base = 'Mükemmel güvenilirlik (çok yüksek tutarlılık).'
  } else if (value >= 0.8) {
    base = 'İyi güvenilirlik.'
  } else if (value >= 0.7) {
    base = 'Kabul edilebilir güvenilirlik.'
  } else if (value >= 0.6) {
    base = 'Sorgulanabilir/sınırda güvenilirlik (iyileştirme önerilir).'
  } else if (value >= 0.4) {
    base = 'Düşük güvenilirlik (geliştirme gereklidir.).'
  } else {
    base = 'Kabul edilemez güvenilirlik (ölçek yeniden tasarlanmalı).'
  }

  const notes = []
  if (typeof questionCount === 'number' && questionCount > 0 && questionCount < 10) {
    notes.push('Madde sayısı az olduğunda alpha doğal olarak düşük çıkabilir (k<10).')
  }
  if (value >= 0.95) {
    notes.push('Aşırı benzer maddeleri azaltmak veya kapsamı genişletmek düşünülebilir.')
  } else if (value < 0.6) {
    notes.push('Düşük madde-toplam korelasyonlu maddeleri ayıklamayı ve madde sayısını artırmayı düşünün.')
  }

  return notes.length ? `${base} ${notes.join(' ')}` : base
}

const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return '-'
  }

  const numeric = parseNumericValue(value)
  if (numeric === null) {
    return '-'
  }

  const rounded = Math.round((numeric + Number.EPSILON) * 100) / 100

  return Math.abs(rounded % 1) < 0.0001 ? rounded.toString() : rounded.toFixed(2)
}

const mapOrderToLetter = (order) => {
  const numeric = Number(order)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null
  }

  const integerOrder = Math.round(numeric)
  if (integerOrder <= 0 || Math.abs(integerOrder - numeric) > 0.0001) {
    return null
  }

  const baseCode = 'A'.charCodeAt(0)
  const maxOffset = 'Z'.charCodeAt(0) - baseCode
  const offset = integerOrder - 1
  if (offset < 0 || offset > maxOffset) {
    return null
  }

  return String.fromCharCode(baseCode + offset)
}

const mapLetterToOrder = (letter) => {
  if (letter === null || letter === undefined) {
    return null
  }

  const raw = typeof letter === 'number' ? String(letter) : String(letter)
  const trimmed = raw.trim()
  if (trimmed.length !== 1) {
    return null
  }

  const normalized = trimmed.toUpperCase()
  const baseCode = 'A'.charCodeAt(0)
  const code = normalized.charCodeAt(0)
  const offset = code - baseCode + 1

  if (offset < 1 || offset > 26) {
    return null
  }

  return offset
}

const normalizeOptionValue = (value) => {
  if (value === null || value === undefined) {
    return null
  }

  const raw = typeof value === 'number' ? String(value) : String(value)
  const trimmed = raw.trim()
  if (!trimmed) {
    return null
  }

  const upper = trimmed.toUpperCase()
  if (/^\d+$/.test(upper)) {
    const mappedLetter = mapOrderToLetter(Number(upper))
    if (mappedLetter) {
      return mappedLetter
    }
  }

  return upper
}

const normalizeOptionIdentifier = (value) => {
  if (value === null || value === undefined) {
    return null
  }

  const raw = typeof value === 'number' ? String(value) : String(value)
  const trimmed = raw.trim()
  if (!trimmed) {
    return null
  }

  return trimmed.toUpperCase()
}

const MASTER_BOOKLET_CODE_ALIASES = new Set([
  '',
  'MASTER',
  '__MASTER__',
  'MASTER KITAPÇIK',
  'MASTER KITAPCIK',
  'MASTER KİTAPÇIK',
  'MASTER BOOKLET',
])

const isMasterBookletCode = (code) => {
  if (code === null || code === undefined) {
    return true
  }

  const raw = typeof code === 'string' ? code : String(code)
  const normalized = raw.trim().toUpperCase()
  return MASTER_BOOKLET_CODE_ALIASES.has(normalized)
}

const normalizeAcceptedOptionsList = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return []
  }

  return Array.from(
    new Set(
      options
        .map((option) => normalizeOptionValue(option))
        .filter((option) => Boolean(option))
    )
  )
}

const normalizeBookletCode = (code) => {
  if (code === null || code === undefined) {
    return ''
  }

  if (isMasterBookletCode(code)) {
    return ''
  }

  const raw = typeof code === 'string' ? code : String(code)
  const trimmed = raw.trim()
  if (!trimmed) {
    return ''
  }

  return trimmed.toUpperCase()
}

const resolveBookletLabel = (code) => {
  if (code === null || code === undefined) {
    return 'Master Kitapçık'
  }

  if (isMasterBookletCode(code)) {
    return 'Master Kitapçık'
  }

  if (typeof code === 'string') {
    const trimmed = code.trim()
    if (trimmed) {
      return trimmed
    }
  }

  return 'Master Kitapçık'
}

const resolveStudentBookletCode = (student) => {
  if (!student || typeof student !== 'object') {
    return ''
  }

  const candidateValues = [
    student.bookletCode,
    student.booklet,
    student.bookletName,
    student.bookletLabel,
    student.bookletCodeLabel,
  ]

  for (const value of candidateValues) {
    if (value === undefined || value === null) {
      continue
    }

    if (isMasterBookletCode(value)) {
      return ''
    }

    const normalized = normalizeBookletCode(value)
    if (normalized) {
      return normalized
    }
  }

  const answerCollections = []
  if (Array.isArray(student.answers)) {
    answerCollections.push(student.answers)
  }
  if (Array.isArray(student.Answers)) {
    answerCollections.push(student.Answers)
  }

  for (const collection of answerCollections) {
    for (const answer of collection) {
      if (!answer || typeof answer !== 'object') {
        continue
      }

      const candidate = answer.bookletCode ?? answer.booklet
      if (candidate === undefined || candidate === null) {
        continue
      }

      if (isMasterBookletCode(candidate)) {
        return ''
      }

      const normalized = normalizeBookletCode(candidate)
      if (normalized) {
        return normalized
      }
    }
  }

  return ''
}

const normalizeStudentAnswers = (student) => {
  const rawAnswers = Array.isArray(student?.answers)
    ? student.answers
    : Array.isArray(student?.Answers)
    ? student.Answers
    : []

  return rawAnswers
    .map((answer) => {
      if (!answer || typeof answer !== 'object') {
        return null
      }

      const examQuestionId =
        typeof answer.examQuestionId === 'number'
          ? answer.examQuestionId
          : typeof answer.ExamQuestionId === 'number'
          ? answer.ExamQuestionId
          : null

      const selectedOptionIds = Array.isArray(answer.selectedOptionIds)
        ? answer.selectedOptionIds
        : Array.isArray(answer.SelectedOptionIds)
        ? answer.SelectedOptionIds
        : []

      const answerText =
        typeof answer.answerText === 'string'
          ? answer.answerText
          : typeof answer.AnswerText === 'string'
          ? answer.AnswerText
          : ''

      const score =
        normalizeScoreValue(answer.score) !== null
          ? normalizeScoreValue(answer.score)
          : normalizeScoreValue(answer.Score)

      return {
        ...answer,
        examQuestionId,
        selectedOptionIds,
        answerText,
        score,
      }
    })
    .filter(Boolean)
}

const deriveAcceptedOptionsFromItems = (items) => {
  if (!Array.isArray(items)) {
    return []
  }

  const options = []

  items.forEach((item) => {
    if (!item || !item.isTrue) {
      return
    }

    const orderValue = item.order
    const label = resolveQuestionItemLabel(item, options.length)
    if (label) {
      options.push(label)
    }
  })

  return normalizeAcceptedOptionsList(options)
}

const resolveQuestionItemLabel = (item, fallbackIndex = 0) => {
  if (item) {
    if (typeof item.key === 'string' && item.key.trim().length > 0) {
      const normalizedKey = normalizeOptionValue(item.key)
      if (normalizedKey) {
        return normalizedKey
      }
    }

    if (typeof item.letter === 'string' && item.letter.trim().length > 0) {
      const normalizedLetter = normalizeOptionValue(item.letter)
      if (normalizedLetter) {
        return normalizedLetter
      }
    }

    const orderValue = Number(item.order)
    if (Number.isFinite(orderValue) && orderValue > 0) {
      const orderLetter = mapOrderToLetter(orderValue)
      if (orderLetter) {
        return orderLetter
      }
    }
  }

  const fallbackLetter = mapOrderToLetter(fallbackIndex + 1)
  if (fallbackLetter) {
    return fallbackLetter
  }

  const numericIndex = Number(fallbackIndex)
  if (Number.isFinite(numericIndex)) {
    return String(numericIndex + 1)
  }

  return null
}

const extractOptionLabelsFromAnswerText = (answerText) => {
  if (typeof answerText !== 'string') {
    return []
  }

  const sanitized = answerText.replace(/<[^>]*>/g, ' ').trim()
  if (!sanitized) {
    return []
  }

  if (!/^[0-9A-Za-zÇĞİÖŞÜçğıöşü,;\s]+$/.test(sanitized)) {
    return []
  }

  const rawCandidates = sanitized
    .split(/[,;\s]+/)
    .map((candidate) => candidate.trim())
    .filter((candidate) => candidate.length > 0)

  if (rawCandidates.length === 0) {
    return []
  }

  const validCandidates = rawCandidates.filter((candidate) => {
    const upper = candidate.toUpperCase()

    if (/^\d+$/.test(upper)) {
      return true
    }

    return /^[A-ZÇĞİÖŞÜ]$/.test(upper)
  })

  if (validCandidates.length !== rawCandidates.length) {
    return []
  }

  return normalizeAcceptedOptionsList(validCandidates)
}

const parseAcceptedOptionsInput = (value) => {
  if (!value) {
    return []
  }

  const tokens = value
    .split(/[;,\s]+/)
    .map((token) => normalizeOptionValue(token))
    .filter((token) => Boolean(token))

  return Array.from(new Set(tokens))
}

const formatAcceptedOptionsInput = (rawValue, normalizedOptions) => {
  const canonicalValue = normalizedOptions.join(', ')
  if (!rawValue) {
    return canonicalValue
  }

  if (normalizedOptions.length > 0 && /[;,\s]+$/.test(rawValue)) {
    return `${canonicalValue}, `
  }

  return canonicalValue
}

const stripWrappingParagraphTags = (html) => {
  const trimmed = typeof html === 'string' ? html.trim() : ''
  if (!trimmed) {
    return ''
  }

  const match = trimmed.match(/^<p[^>]*>([\s\S]*)<\/p>$/i)
  if (match) {
    return match[1].trim()
  }

  return trimmed
}

const removeTrailingEmptyHtml = (html) => {
  let output = typeof html === 'string' ? html.trim() : ''
  if (!output) {
    return ''
  }

  const trailingPattern =
    /(?:\s|&nbsp;|&#160;|<br\s*\/?>|<p[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>)+$/gi

  while (true) {
    const next = output.replace(trailingPattern, '').trim()
    if (next === output) {
      break
    }
    output = next
  }

  return output
}

const enhanceQuestionHtml = (html) => {
  if (!html) {
    return ''
  }

  let normalized = html.trim()
  normalized = removeTrailingEmptyHtml(normalized)
  normalized = stripWrappingParagraphTags(normalized)
  normalized = removeTrailingEmptyHtml(normalized)

  return normalized
}

const escapeHtml = (value) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const formatLineWithBasicMarkdown = (value) =>
  escapeHtml(value).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

const formatAiCommentHtml = (comment) => {
  const normalized = (comment || '').replace(/\r\n/g, '\n').trim()
  if (!normalized) {
    return "<p class='mb-0 text-muted'>Yapay zekâdan yorum alınamadı.</p>"
  }

  const lines = normalized.split('\n')
  const blocks = []
  let currentList = null

  const flushList = () => {
    if (!currentList || currentList.items.length === 0) {
      currentList = null
      return
    }

    const tag = currentList.type
    const items = currentList.items
      .map((item) => `<li class='mb-1'>${item}</li>`)
      .join('')
    blocks.push(`<${tag} class='mb-2 ps-4'>${items}</${tag}>`)
    currentList = null
  }

  lines.forEach((line) => {
    const trimmed = line.trim()
    if (!trimmed) {
      flushList()
      return
    }

    const orderedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/)
    if (orderedMatch) {
      const content = formatLineWithBasicMarkdown(orderedMatch[2])
      if (!currentList || currentList.type !== 'ol') {
        flushList()
        currentList = {type: 'ol', items: []}
      }
      currentList.items.push(content)
      return
    }

    if (/^[-•]\s+/.test(trimmed)) {
      const content = formatLineWithBasicMarkdown(trimmed.replace(/^[-•]\s+/, ''))
      if (!currentList || currentList.type !== 'ul') {
        flushList()
        currentList = {type: 'ul', items: []}
      }
      currentList.items.push(content)
      return
    }

    flushList()
    blocks.push(`<p class='mb-2'>${formatLineWithBasicMarkdown(trimmed)}</p>`)
  })

  flushList()

  if (blocks.length === 0) {
    return "<p class='mb-0 text-muted'>Yapay zekâdan yorum alınamadı.</p>"
  }

  const lastIndex = blocks.length - 1
  blocks[lastIndex] = blocks[lastIndex].replace('mb-2', 'mb-0')

  return blocks.join('')
}

const decodeHtmlEntities = (value) =>
  value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")

const sanitizeRichText = (value) => {
  if (!value) {
    return ''
  }

  const enhanced = enhanceQuestionHtml(value)
  const withoutTags = enhanced.replace(/<[^>]*>/g, ' ')
  return decodeHtmlEntities(withoutTags).replace(/\s+/g, ' ').trim()
}

const clampScore = (value, maxValue) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return 0
  }

  let sanitized = numeric
  if (sanitized < 0) {
    sanitized = 0
  }

  if (typeof maxValue === 'number' && Number.isFinite(maxValue)) {
    sanitized = Math.min(sanitized, maxValue)
  }

  return Math.round(sanitized * 100) / 100
}

const resolveStudentFaculty = (student) => {
  if (typeof student?.facultyName === 'string' && student.facultyName.trim().length > 0) {
    return student.facultyName
  }

  if (typeof student?.faculty === 'string' && student.faculty.trim().length > 0) {
    return student.faculty
  }

  if (typeof student?.facultyTitle === 'string' && student.facultyTitle.trim().length > 0) {
    return student.facultyTitle
  }

  if (student?.faculty?.name) {
    return student.faculty.name
  }

  return '-'
}

const resolveStudentDepartment = (student) => {
  if (typeof student?.departmentName === 'string' && student.departmentName.trim().length > 0) {
    return student.departmentName
  }

  if (typeof student?.department === 'string' && student.department.trim().length > 0) {
    return student.department
  }

  if (typeof student?.programName === 'string' && student.programName.trim().length > 0) {
    return student.programName
  }

  if (student?.department?.name) {
    return student.department.name
  }

  return '-'
}

const normalizeScoreValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const numeric = Number(value)
    if (Number.isFinite(numeric)) {
      return numeric
    }
  }

  return null
}

const ExamResultsInput = () => {
  const location = useLocation()
  const stateValue = location.state
  const normalizedExamId =
    typeof stateValue === 'number' && Number.isFinite(stateValue) ? stateValue : Number(stateValue)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasInitialData, setHasInitialData] = useState(false)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [questionLoading, setQuestionLoading] = useState(true)
  const [error, setError] = useState(null)
  const [overviewError, setOverviewError] = useState(null)
  const [questionError, setQuestionError] = useState(null)
  const [studentData, setStudentData] = useState(null)
  const [studentLoading, setStudentLoading] = useState(false)
  const [studentError, setStudentError] = useState(null)
  const [scores, setScores] = useState({})
  const [saving, setSaving] = useState(false)
  const [previewStudentId, setPreviewStudentId] = useState(null)
  const [questionPreviewContext, setQuestionPreviewContext] = useState(null)
  const [answerKeyState, setAnswerKeyState] = useState({})
  const [answerKeyInputs, setAnswerKeyInputs] = useState({})
  const [pendingAnswerKeyUpdates, setPendingAnswerKeyUpdates] = useState({})
  const [answerKeySyncing, setAnswerKeySyncing] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [penaltyState, setPenaltyState] = useState({
    applyPenalty: false,
    wrongCount: 4,
    correctDeduction: 1,
  })
  const [nameFilterInput, setNameFilterInput] = useState('')
  const [numberFilterInput, setNumberFilterInput] = useState('')
  const [appliedNameFilter, setAppliedNameFilter] = useState('')
  const [appliedNumberFilter, setAppliedNumberFilter] = useState('')
  const [sortField, setSortField] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [penaltyDirty, setPenaltyDirty] = useState(false)
  const [cancellationDirty, setCancellationDirty] = useState(false)
  const [questionAiReviewCache, setQuestionAiReviewCache] = useState({})
  const [autoReviewPendingMap, setAutoReviewPendingMap] = useState({})
  const [questionCancellationState, setQuestionCancellationState] = useState({})
  const [obsSending, setObsSending] = useState(false)
  const [initialStudentLoadCompleted, setInitialStudentLoadCompleted] = useState(false)
  const [studentDetailsCache, setStudentDetailsCache] = useState({})
  const [bookletEvaluationContext, setBookletEvaluationContext] = useState(null)
  const [bookletEvaluationLoading, setBookletEvaluationLoading] = useState(false)
  const [bookletEvaluationApplying, setBookletEvaluationApplying] = useState(null)

  const summary = data?.summary ?? null
  const questions = useMemo(
    () => (Array.isArray(data?.questions) ? data.questions : []),
    [data?.questions]
  )
  const multipleChoiceQuestionAnalyses = useMemo(() => {
    if (!Array.isArray(summary?.questionAnalyses) || summary.questionAnalyses.length === 0) {
      return []
    }

    if (questions.length === 0) {
      return []
    }

    const allowedIds = new Set(
      questions
        .filter((question) => question && typeof question.id === 'number' && question.isMultipleChoice)
        .map((question) => question.id)
    )

    const questionOrderMap = questions.reduce((acc, question) => {
      if (!question || typeof question.id !== 'number') {
        return acc
      }

      const numericOrder = Number(question.order)
      acc[question.id] = Number.isFinite(numericOrder) ? numericOrder : Number.MAX_SAFE_INTEGER
      return acc
    }, {})

    return summary.questionAnalyses
      .filter(
      (analysis) =>
        analysis && typeof analysis.examQuestionId === 'number' && allowedIds.has(analysis.examQuestionId)
      )
      .sort((a, b) => {
        const orderA = questionOrderMap[a.examQuestionId] ?? Number.MAX_SAFE_INTEGER
        const orderB = questionOrderMap[b.examQuestionId] ?? Number.MAX_SAFE_INTEGER

        if (orderA !== orderB) {
          return orderA - orderB
        }

        return a.examQuestionId - b.examQuestionId
      })
  }, [summary?.questionAnalyses, questions])

  const answerKeyStateRef = useRef({})
  const pendingAnswerKeyUpdatesRef = useRef({})
  const answerKeySyncTimeoutRef = useRef(null)
  const penaltyStateRef = useRef(penaltyState)
  const penaltyDirtyRef = useRef(penaltyDirty)
  const cancellationDirtyRef = useRef(cancellationDirty)
  const pendingUpdateCounterRef = useRef(0)
  const answerKeySyncModalOpenRef = useRef(false)
  const autoReviewTriggeredRef = useRef({})
  const autoReviewInProgressRef = useRef(false)
  const questionAiReviewCacheRef = useRef({})
  const questionCancellationStateRef = useRef({})
  const initialQuestionCancellationStateRef = useRef({})
  const selectedCancelledQuestionIdsRef = useRef([])
  const selectedRecoveredQuestionIdsRef = useRef([])
  const studentLoadingModalOpenRef = useRef(false)

  const openStudentLoadingModal = useCallback(() => {
    if (studentLoadingModalOpenRef.current) {
      return
    }

    studentLoadingModalOpenRef.current = true
    Swal.fire({
      title: 'Öğrenciler yükleniyor',
      text: 'Lütfen bekleyin... bazen birkaç saniye sürebilir.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })
  }, [])

  const closeStudentLoadingModal = useCallback(() => {
    if (!studentLoadingModalOpenRef.current) {
      return
    }

    Swal.close()
    studentLoadingModalOpenRef.current = false
  }, [])

  const openAnswerKeySyncModal = useCallback(() => {
    if (answerKeySyncModalOpenRef.current) {
      return
    }

    answerKeySyncModalOpenRef.current = true
    Swal.fire({
      title: 'Cevap anahtarı güncelleniyor',
      text: 'Lütfen bekleyin...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })
  }, [])

  const closeAnswerKeySyncModal = useCallback(() => {
    if (!answerKeySyncModalOpenRef.current) {
      return
    }

    Swal.close()
    answerKeySyncModalOpenRef.current = false
  }, [])

  const updateQuestionAiReviewCache = useCallback((updater) => {
    setQuestionAiReviewCache((prev) => {
      const next = typeof updater === 'function' ? updater(prev || {}) : updater || {}
      questionAiReviewCacheRef.current = next
      return next
    })
  }, [])

  useEffect(() => {
    questionAiReviewCacheRef.current = questionAiReviewCache || {}
  }, [questionAiReviewCache])

  useEffect(() => {
    questionCancellationStateRef.current = questionCancellationState || {}
  }, [questionCancellationState])

  useEffect(() => {
    const baseState = initialQuestionCancellationStateRef.current || {}

    const selectedIds = Object.entries(questionCancellationState || {})
      .filter(([, value]) => Boolean(value))
      .map(([id]) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)

    selectedCancelledQuestionIdsRef.current = Array.from(new Set(selectedIds))

    const recoveredIds = Object.entries(questionCancellationState || {})
      .filter(([id, value]) => {
        const numericId = Number(id)
        if (!Number.isFinite(numericId)) {
          return false
        }

        return Boolean(baseState?.[numericId]) && !Boolean(value)
      })
      .map(([id]) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)

    selectedRecoveredQuestionIdsRef.current = Array.from(new Set(recoveredIds))
  }, [questionCancellationState])

  useEffect(() => {
    if (multipleChoiceQuestionAnalyses.length === 0) {
      return
    }

    updateQuestionAiReviewCache((previous) => {
      const next = {...(previous || {})}
      let changed = false

      multipleChoiceQuestionAnalyses.forEach((analysis) => {
        if (!analysis || typeof analysis.examQuestionId !== 'number') {
          return
        }

        const rawComment =
          typeof analysis.aiReviewComment === 'string' ? analysis.aiReviewComment.trim() : ''
        if (!rawComment) {
          return
        }

        const cacheEntry = next[analysis.examQuestionId] || null
        const normalizedSignature =
          typeof analysis.aiReviewSignature === 'string' && analysis.aiReviewSignature.trim().length > 0
            ? analysis.aiReviewSignature.trim()
            : null
        const updatedAt = analysis.aiReviewUpdatedAt || null

        const formattedHtml = formatAiCommentHtml(rawComment)

        if (
          !cacheEntry ||
          cacheEntry.comment !== rawComment ||
          cacheEntry.signature !== normalizedSignature ||
          (updatedAt && cacheEntry.updatedAt !== updatedAt) ||
          !cacheEntry.formattedHtml
        ) {
          next[analysis.examQuestionId] = {
            comment: rawComment,
            formattedHtml,
            signature: normalizedSignature,
            updatedAt,
          }
          changed = true
        }
      })

      return changed ? next : previous
    })
  }, [multipleChoiceQuestionAnalyses, updateQuestionAiReviewCache])

  const showAnswerKeySuccessAlert = useCallback((message) => {
    const resolvedMessage = typeof message === 'string' && message.trim().length > 0
      ? message.trim()
      : 'Cevap anahtarı başarıyla güncellendi.'

    Swal.fire({
      icon: 'success',
      title: 'Başarılı',
      text: resolvedMessage,
      timer: 2000,
      showConfirmButton: false,
    })
  }, [])

  const buildStudentRequestOptions = useCallback(
    (overrides = {}) => {
      const config = typeof overrides === 'object' && overrides ? overrides : {}

      return {
        pageNumber: typeof config.pageNumber === 'number' ? config.pageNumber : pageNumber,
        pageSize: typeof config.pageSize === 'number' ? config.pageSize : pageSize,
        nameFilter: typeof config.nameFilter === 'string' ? config.nameFilter : appliedNameFilter,
        numberFilter: typeof config.numberFilter === 'string' ? config.numberFilter : appliedNumberFilter,
        sortField: typeof config.sortField === 'string' ? config.sortField : sortField,
        sortDirection: typeof config.sortDirection === 'string' ? config.sortDirection : sortDirection,
        silent: Boolean(config.silent),
      }
    },
    [appliedNameFilter, appliedNumberFilter, pageNumber, pageSize, sortDirection, sortField]
  )

  const loadOverview = useCallback(
    async ({silent = false} = {}) => {
      if (!Number.isFinite(normalizedExamId)) {
        setOverviewError('Geçersiz sınav bilgisi.')
        if (!silent) {
          setOverviewLoading(false)
        }
        return
      }

      try {
        if (!silent) {
          setOverviewLoading(true)
        }
        setOverviewError(null)
        const response = await getExamResultStatistics(normalizedExamId)
        setData((prev) => {
          const next = prev || {}
          return {
            ...next,
            exam: response?.exam ?? next.exam,
            summary: response?.summary ?? next.summary,
            obsTransfer: response?.obsTransfer ?? next.obsTransfer,
            penaltyOptions: response?.penaltyOptions ?? next.penaltyOptions,
          }
        })
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          'Sınav bilgileri yüklenirken bir hata oluştu.'
        setOverviewError(message)
      } finally {
        if (!silent) {
          setOverviewLoading(false)
        }
      }
    },
    [normalizedExamId]
  )

  const loadQuestionSection = useCallback(
    async ({silent = false} = {}) => {
      if (!Number.isFinite(normalizedExamId)) {
        setQuestionError('Geçersiz sınav bilgisi.')
        if (!silent) {
          setQuestionLoading(false)
        }
        return
      }

      try {
        if (!silent) {
          setQuestionLoading(true)
        }
        setQuestionError(null)
        const response = await getExamResultQuestions(normalizedExamId)
        setData((prev) => {
          const next = prev || {}
          return {
            ...next,
            questions: Array.isArray(response?.questions) ? response.questions : next.questions,
            booklets: Array.isArray(response?.booklets) ? response.booklets : next.booklets,
          }
        })
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          'Soru listesi yüklenirken bir hata oluştu.'
        setQuestionError(message)
      } finally {
        if (!silent) {
          setQuestionLoading(false)
        }
      }
    },
    [normalizedExamId]
  )

  const loadStudentData = useCallback(
    async (options = {}) => {
      const config = buildStudentRequestOptions(options)
      const {silent = false, ...requestOptions} = config

      if (!Number.isFinite(normalizedExamId)) {
        setStudentError('Geçersiz sınav bilgisi.')
        if (!silent) {
          setStudentLoading(false)
        }
        return
      }

      try {
        if (!silent) {
          openStudentLoadingModal()
          setStudentLoading(true)
        }
        setStudentError(null)
        const response = await getExamResultStudents(normalizedExamId, requestOptions)
        const studentContainer = response?.students ?? response
        setStudentData(studentContainer)

        if (!initialStudentLoadCompleted) {
          setInitialStudentLoadCompleted(true)
        }

        const responsePageNumber =
          studentContainer?.pageNumber ??
          studentContainer?.PageNumber ??
          response?.pageNumber ??
          response?.PageNumber
        if (typeof responsePageNumber === 'number' && responsePageNumber !== pageNumber) {
          setPageNumber(responsePageNumber)
        }

        const responsePageSize =
          studentContainer?.pageSize ??
          studentContainer?.PageSize ??
          response?.pageSize ??
          response?.PageSize
        if (typeof responsePageSize === 'number' && responsePageSize !== pageSize) {
          setPageSize(responsePageSize)
        }
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          'Öğrenci listesi yüklenirken bir hata oluştu.'
        setStudentError(message)
      } finally {
        if (!silent) {
          closeStudentLoadingModal()
          setStudentLoading(false)
        }
      }
    },
    [
      closeStudentLoadingModal,
      buildStudentRequestOptions,
      initialStudentLoadCompleted,
      normalizedExamId,
      pageNumber,
      pageSize,
      openStudentLoadingModal,
    ]
  )

  const loadData = useCallback(
    async (options = {}) => {
      const {
        silentOverview = false,
        silentQuestions = false,
        studentOptions = options,
      } = options

      await Promise.all([
        loadOverview({silent: silentOverview}),
        loadQuestionSection({silent: silentQuestions}),
        loadStudentData(studentOptions),
      ])
    },
    [loadOverview, loadQuestionSection, loadStudentData]
  )

  useEffect(() => {
    setError(overviewError || questionError || null)
  }, [overviewError, questionError])

  useEffect(() => {
    setLoading(overviewLoading || questionLoading)
  }, [overviewLoading, questionLoading])

  useEffect(() => {
    if (!overviewLoading && !questionLoading) {
      setHasInitialData(true)
    }
  }, [overviewLoading, questionLoading])

  useEffect(() => {
    if (!Number.isFinite(normalizedExamId)) {
      return
    }

    setInitialStudentLoadCompleted(false)
    setStudentData(null)
    setStudentError(null)

    const initialRequestOptions = {
      pageNumber,
      pageSize,
      nameFilter: appliedNameFilter,
      numberFilter: appliedNumberFilter,
      sortField,
      sortDirection,
    }

    loadOverview()
    loadQuestionSection()
    loadStudentData(initialRequestOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedExamId])

  useEffect(() => {
    if (!initialStudentLoadCompleted) {
      return
    }

    loadStudentData({
      pageNumber,
      pageSize,
      nameFilter: appliedNameFilter,
      numberFilter: appliedNumberFilter,
      sortField,
      sortDirection,
    })
  }, [
    appliedNameFilter,
    appliedNumberFilter,
    initialStudentLoadCompleted,
    loadStudentData,
    pageNumber,
    pageSize,
    sortDirection,
    sortField,
  ])

  const exam = data?.exam ?? null
  const obsTransfer = data?.obsTransfer ?? null
  const obsTargetOptions = useMemo(() => {
    if (!obsTransfer || !Array.isArray(obsTransfer.targets)) {
      return []
    }

    return obsTransfer.targets
      .map((item) => {
        const code = typeof item?.code === 'string' ? item.code.trim() : ''
        if (!code) {
          return null
        }

        const label =
          typeof item?.label === 'string' && item.label.trim().length > 0
            ? item.label.trim()
            : code

        return {code, label}
      })
      .filter(Boolean)
  }, [obsTransfer])

  const requiresObsTargetSelection = useMemo(() => {
    if (!obsTransfer) {
      return false
    }

    if (typeof obsTransfer.requiresTargetSelection === 'boolean') {
      return obsTransfer.requiresTargetSelection
    }

    return obsTargetOptions.length > 1
  }, [obsTransfer, obsTargetOptions])

  const questionById = useMemo(() => {
    const lookup = {}
    questions.forEach((question) => {
      if (question && typeof question.id === 'number') {
        lookup[question.id] = question
      }
    })
    return lookup
  }, [questions])

  useEffect(() => {
    const penaltyCancelledIds = new Set(
      Array.isArray(data?.penaltyOptions?.cancelledQuestionIds)
        ? data.penaltyOptions.cancelledQuestionIds
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
        : []
    )

    const penaltyRecoveredIds = new Set(
      Array.isArray(data?.penaltyOptions?.recoveredQuestionIds)
        ? data.penaltyOptions.recoveredQuestionIds
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
        : []
    )

    const nextState = {}
    questions.forEach((question) => {
      if (question && typeof question.id === 'number') {
        const normalizedId = question.id
        const isCancelled =
          (Boolean(question.isCancelled) || penaltyCancelledIds.has(normalizedId)) &&
          !penaltyRecoveredIds.has(normalizedId)
        nextState[normalizedId] = isCancelled
      }
    })

    penaltyCancelledIds.forEach((questionId) => {
      if (!Object.prototype.hasOwnProperty.call(nextState, questionId)) {
        nextState[questionId] = !penaltyRecoveredIds.has(questionId)
      }
    })

    initialQuestionCancellationStateRef.current = nextState
    setQuestionCancellationState(nextState)
    questionCancellationStateRef.current = nextState
  }, [questions, data?.penaltyOptions?.cancelledQuestionIds, data?.penaltyOptions?.recoveredQuestionIds])
  const students = useMemo(() => {
    const source = studentData ?? data

    if (Array.isArray(source?.students)) {
      return source.students
    }

    if (Array.isArray(source?.students?.items)) {
      return source.students.items
    }

    if (Array.isArray(source?.students?.data)) {
      return source.students.data
    }

    if (Array.isArray(source?.items)) {
      return source.items
    }

    return []
  }, [data, studentData])

  const studentPageInfo = useMemo(() => {
    const source = studentData ?? data

    if (source?.students && !Array.isArray(source.students)) {
      return source.students
    }

    const totalFallback = [
      source?.totalCount,
      source?.TotalCount,
      source?.count,
      source?.Count,
      source?.totalStudentCount,
      source?.TotalStudentCount,
      source?.studentsTotalCount,
      source?.StudentsTotalCount,
      source?.totalStudents,
      source?.TotalStudents,
    ]
      .map((value) => normalizeScoreValue(value))
      .find((value) => value !== null)

    if (totalFallback === null) {
      return null
    }

    const normalizedPageNumber = normalizeScoreValue(source?.pageNumber ?? source?.PageNumber)
    const normalizedPageSize = normalizeScoreValue(source?.pageSize ?? source?.PageSize ?? pageSize)

    return {
      totalCount: totalFallback,
      pageNumber: normalizedPageNumber || 1,
      pageSize: normalizedPageSize || pageSize,
    }
  }, [data, pageSize, studentData])

  const opticalObjectionNote = useMemo(() => {
    const noteValue = exam?.opticalObjectionNote ?? exam?.OpticalObjectionNote

    if (typeof noteValue !== 'string') {
      return ''
    }

    return noteValue.trim()
  }, [exam?.OpticalObjectionNote, exam?.opticalObjectionNote])
  const remoteTotalCount = useMemo(() => {
    const candidates = [
      studentPageInfo?.totalCount,
      studentPageInfo?.TotalCount,
      studentPageInfo?.count,
      studentPageInfo?.Count,
    ]
    const normalized = candidates
      .map((value) => normalizeScoreValue(value))
      .find((value) => value !== null)

    return normalized ?? null
  }, [studentPageInfo?.Count, studentPageInfo?.TotalCount, studentPageInfo?.count, studentPageInfo?.totalCount])

  const remotePageSize = useMemo(() => {
    const candidates = [studentPageInfo?.pageSize, studentPageInfo?.PageSize]
    const normalized = candidates
      .map((value) => normalizeScoreValue(value))
      .find((value) => value !== null)

    return normalized ?? null
  }, [studentPageInfo?.PageSize, studentPageInfo?.pageSize])

  const useServerPaging = typeof remoteTotalCount === 'number'

  const effectivePageSize = useMemo(() => {
    if (typeof remotePageSize === 'number' && Number.isFinite(remotePageSize) && remotePageSize > 0) {
      return remotePageSize
    }

    return pageSize
  }, [pageSize, remotePageSize])

  const totalStudentCount = useMemo(() => {
    if (typeof remoteTotalCount === 'number') {
      return remoteTotalCount
    }

    return students.length
  }, [remoteTotalCount, students.length])
  const studentResultLookup = useMemo(() => {
    const lookup = {}

    students.forEach((student) => {
      const studentId = student?.studentSemesterCourseId
      if (!studentId) {
        return
      }

      const questionMap = {}
      if (Array.isArray(student?.examResults)) {
        student.examResults.forEach((result) => {
          const questionId = result?.examQuestionId
          if (typeof questionId === 'number') {
            questionMap[questionId] = result
          }
        })
      }

      lookup[studentId] = questionMap
    })

    return lookup
  }, [students])
  const booklets = useMemo(
    () => (Array.isArray(data?.booklets) ? data.booklets : []),
    [data?.booklets]
  )
  const linkedExams = Array.isArray(exam?.linkedExams) ? exam.linkedExams : []
  const hasLinkedGroup = linkedExams.length > 1
  const obsDisabledReason = useMemo(() => {
    if (!obsTransfer) {
      return null
    }

    if (requiresObsTargetSelection && obsTargetOptions.length === 0) {
      return 'OBS sınav seçenekleri yüklenemedi.'
    }

    if (typeof obsTransfer.disabledReason === 'string') {
      const trimmed = obsTransfer.disabledReason.trim()
      if (trimmed.length > 0) {
        return trimmed
      }
    }

    return null
  }, [obsTransfer, obsTargetOptions, requiresObsTargetSelection])

  const canSendObs = useMemo(() => {
    if (!obsTransfer?.isObsEnabled || !obsTransfer?.isActivityOpen) {
      return false
    }

    if (requiresObsTargetSelection && obsTargetOptions.length === 0) {
      return false
    }

    return true
  }, [obsTransfer, requiresObsTargetSelection, obsTargetOptions])

  useEffect(() => {
    if (!isAiActive) {
      return
    }

    if (!Number.isFinite(normalizedExamId)) {
      return
    }

    const analyses = multipleChoiceQuestionAnalyses
    if (analyses.length === 0) {
      const storageKey = `exam-ai-autoreview:${normalizedExamId}`
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(storageKey, 'completed')
      }
      autoReviewTriggeredRef.current[normalizedExamId] = 'completed'
      return
    }

    if (autoReviewInProgressRef.current) {
      return
    }

    const storageKey = `exam-ai-autoreview:${normalizedExamId}`
    const storedSignature =
      typeof window !== 'undefined' ? window.sessionStorage.getItem(storageKey) : null

    const processedQuestions = new Set()

    const tasks = analyses
      .map((analysis) => {
        if (!shouldAutoReviewQuestion(analysis)) {
          return null
        }

        const existingComment =
          typeof analysis.aiReviewComment === 'string' && analysis.aiReviewComment.trim().length > 0
        if (existingComment) {
          return null
        }

        const difficultyInsight = resolveDifficultyInsight(analysis)
        const discriminationInsight = resolveDiscriminationInsight(analysis)

        const reviewData = buildAutoReviewData({
          examId: normalizedExamId,
          analysis,
          question: questionById[analysis.examQuestionId],
          difficultyInsight,
          discriminationInsight,
        })

        if (!reviewData) {
          return null
        }

        const cachedEntry = questionAiReviewCacheRef.current[analysis.examQuestionId]
        if (cachedEntry?.signature === reviewData.signature && cachedEntry.comment) {
          return null
        }

        if (processedQuestions.has(reviewData.questionId)) {
          return null
        }

        processedQuestions.add(reviewData.questionId)
        return reviewData
      })
      .filter(Boolean)

    const hasPendingTasks = tasks.length > 0
    const aggregatedSignature =
      hasPendingTasks
        ? tasks
            .map((task) => `${task.questionId}:${task.signature}`)
            .sort()
            .join('|')
        : 'completed'

    if (!hasPendingTasks) {
      autoReviewTriggeredRef.current[normalizedExamId] = 'completed'
      if (storedSignature !== 'completed' && typeof window !== 'undefined') {
        window.sessionStorage.setItem(storageKey, 'completed')
      }
      return
    }

    const lastTriggeredSignature =
      autoReviewTriggeredRef.current[normalizedExamId] || storedSignature

    if (lastTriggeredSignature === aggregatedSignature) {
      autoReviewTriggeredRef.current[normalizedExamId] = aggregatedSignature
      if (storedSignature !== aggregatedSignature && typeof window !== 'undefined') {
        window.sessionStorage.setItem(storageKey, aggregatedSignature)
      }
      return
    }

    autoReviewTriggeredRef.current[normalizedExamId] = aggregatedSignature
    autoReviewInProgressRef.current = true

    setAutoReviewPendingMap((prev) => {
      const next = {...prev}
      tasks.forEach((task) => {
        next[task.questionId] = true
      })
      return next
    })

    Swal.fire({
      title: 'Yapay zekâya danışılıyor',
      text: 'Sorular analiz için gönderiliyor. Lütfen bekleyin...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })

    let isCancelled = false

    const runAutoReview = async () => {
      const errors = []

      for (const task of tasks) {
        if (isCancelled) {
          break
        }

        try {
          const response = await getOpticalQuestionReview(task.payload)
          const comment = (response?.comment ?? '').trim()
          const normalizedComment = comment.length > 0 ? comment : 'Yapay zekâdan yorum alınamadı.'
          const formattedHtml = formatAiCommentHtml(normalizedComment)

          const timestamp = new Date().toISOString()
          updateQuestionAiReviewCache((prev) => ({
            ...prev,
            [task.questionId]: {
              comment: normalizedComment,
              formattedHtml,
              signature: task.signature,
              updatedAt: timestamp,
            },
          }))
        } catch (error) {
          const message =
            error?.response?.data?.error ||
            error?.response?.data ||
            error?.message ||
            'Yapay zekâ yanıtı alınamadı.'
          const normalizedMessage =
            typeof message === 'string' && message.trim().length > 0
              ? message.trim()
              : 'Yapay zekâ yanıtı alınamadı.'
          errors.push(normalizedMessage)

          const timestamp = new Date().toISOString()
          updateQuestionAiReviewCache((prev) => ({
            ...prev,
            [task.questionId]: {
              error: normalizedMessage,
              signature: task.signature,
              updatedAt: timestamp,
            },
          }))
        } finally {
          setAutoReviewPendingMap((prev) => {
            const next = {...prev}
            delete next[task.questionId]
            return next
          })
        }
      }

      Swal.close()

      if (!isCancelled) {
        if (errors.length > 0) {
          Swal.fire({
            title: 'Yapay zekâ yanıtı alınamadı',
            text: errors[0],
            icon: 'error',
            confirmButtonText: 'Kapat',
          })
        } else {
          Swal.fire({
            title: 'Tamamlandı',
            text: 'Yapay zekâ yorumları hazır.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          })
        }
      }

      if (typeof window !== 'undefined') {
        if (errors.length === 0) {
          window.sessionStorage.setItem(storageKey, aggregatedSignature)
        } else {
          window.sessionStorage.removeItem(storageKey)
        }
      }

      autoReviewInProgressRef.current = false
    }

    runAutoReview()

    return () => {
      isCancelled = true
      autoReviewInProgressRef.current = false
      setAutoReviewPendingMap({})
      Swal.close()
    }
  }, [
    isAiActive,
    normalizedExamId,
    multipleChoiceQuestionAnalyses,
    questionById,
    summary,
    updateQuestionAiReviewCache,
  ])

  useEffect(() => {
    if (!data) {
      return
    }

    const options = data.penaltyOptions
    let nextPenalty
    if (options) {
      nextPenalty = {
        applyPenalty: Boolean(options.applyWrongPenalty),
        wrongCount:
          Number.isFinite(Number(options.wrongCountForPenalty)) && Number(options.wrongCountForPenalty) > 0
            ? Number(options.wrongCountForPenalty)
            : 4,
        correctDeduction:
          Number.isFinite(Number(options.correctCountToSubtract)) && Number(options.correctCountToSubtract) > 0
            ? Number(options.correctCountToSubtract)
            : 1,
      }
    } else {
      nextPenalty = {applyPenalty: false, wrongCount: 4, correctDeduction: 1}
    }
    setPenaltyState(nextPenalty)
    penaltyStateRef.current = nextPenalty
    setPenaltyDirty(false)
    penaltyDirtyRef.current = false
  }, [data?.penaltyOptions, data?.exam?.id])

  const availableBookletCodes = useMemo(() => {
    const codes = ['']
    booklets.forEach((booklet) => {
      const normalized = normalizeBookletCode(booklet?.code)
      if (normalized && !codes.includes(normalized)) {
        codes.push(normalized)
      }
    })

    return codes
  }, [booklets])

  const questionFallbackAcceptedOptions = useMemo(() => {
    const lookup = {}

    questions.forEach((question) => {
      if (typeof question?.id !== 'number') {
        return
      }

      const items = Array.isArray(question?.questionItems) ? question.questionItems : []
      lookup[question.id] = deriveAcceptedOptionsFromItems(items)
    })

    return lookup
  }, [questions])

  useEffect(() => {
    const questionBookletOptions = {}
    const questionCodes = {}

    booklets.forEach((booklet) => {
      const normalizedCode = normalizeBookletCode(booklet?.code)
      const entries = Array.isArray(booklet?.questions) ? booklet.questions : []

      entries.forEach((entry) => {
        if (typeof entry?.examQuestionId !== 'number') {
          return
        }

        if (!questionBookletOptions[entry.examQuestionId]) {
          questionBookletOptions[entry.examQuestionId] = {}
        }
        if (!questionCodes[entry.examQuestionId]) {
          questionCodes[entry.examQuestionId] = new Set()
        }

        questionBookletOptions[entry.examQuestionId][normalizedCode] = normalizeAcceptedOptionsList(
          entry?.acceptedOptions
        )
        questionCodes[entry.examQuestionId].add(normalizedCode)
      })
    })

    const nextState = {}
    const nextInputs = {}

    questions.forEach((question) => {
      if (typeof question?.id !== 'number') {
        return
      }

      const questionId = question.id
      const optionMap = questionBookletOptions[questionId] || {}
      const codesSet = questionCodes[questionId] || new Set()
      const codes = Array.from(codesSet).sort((a, b) => {
        if (a === '' && b !== '') {
          return -1
        }
        if (a !== '' && b === '') {
          return 1
        }
        return (a || '').localeCompare(b || '', 'tr')
      })
      if (!codes.includes('')) {
        codes.unshift('')
      }

      const fallbackMasterOptions = (() => {
        if (Array.isArray(optionMap['']) && optionMap[''].length > 0) {
          return optionMap['']
        }

        const questionFallback = questionFallbackAcceptedOptions[questionId]
        if (Array.isArray(questionFallback) && questionFallback.length > 0) {
          return questionFallback
        }

        const firstNonEmpty = codes
          .filter((code) => code !== '')
          .map((code) => optionMap[code])
          .find((entries) => Array.isArray(entries) && entries.length > 0)

        return Array.isArray(firstNonEmpty) ? firstNonEmpty : []
      })()

      const normalizedMaster = normalizeAcceptedOptionsList(fallbackMasterOptions)
      const bookletMap = {}

      const resolvedCodes = (codes.length > 0 ? codes : ['']).map((code) => (typeof code === 'string' ? code : ''))
      resolvedCodes.forEach((code) => {
        const normalizedCode = code || ''
        if (normalizedCode === '') {
          bookletMap[normalizedCode] = [...normalizedMaster]
          return
        }

        const currentOptions = optionMap[normalizedCode]
        const normalizedOptions = Array.isArray(currentOptions) && currentOptions.length > 0
          ? normalizeAcceptedOptionsList(currentOptions)
          : normalizedMaster
        bookletMap[normalizedCode] = [...normalizedOptions]
      })

      nextState[questionId] = {
        master: [...normalizedMaster],
        booklets: bookletMap,
        codes: resolvedCodes,
      }
      nextInputs[questionId] = normalizedMaster.join(', ')
    })

    const pendingMap = pendingAnswerKeyUpdatesRef.current || {}
    const pendingKeys = Object.keys(pendingMap || {})

    if (pendingKeys.length === 0) {
      setAnswerKeyState(() => {
        answerKeyStateRef.current = nextState
        return nextState
      })
      setAnswerKeyInputs(nextInputs)
      setPendingAnswerKeyUpdates((prev) => {
        if (Object.keys(prev || {}).length === 0) {
          pendingAnswerKeyUpdatesRef.current = prev || {}
          return prev
        }
        pendingAnswerKeyUpdatesRef.current = {}
        return {}
      })
      return
    }

    const pendingKeySet = new Set(pendingKeys)
    const questionIdSet = new Set(Object.keys(nextState))

    setAnswerKeyState((prev) => {
      const merged = {...prev}

      Object.keys(merged).forEach((key) => {
        if (!questionIdSet.has(key) && !pendingKeySet.has(key)) {
          delete merged[key]
        }
      })

      Object.entries(nextState).forEach(([key, value]) => {
        if (pendingKeySet.has(key)) {
          return
        }
        merged[key] = value
      })

      answerKeyStateRef.current = merged
      return merged
    })

    setAnswerKeyInputs((prev) => {
      const merged = {...prev}

      Object.keys(merged).forEach((key) => {
        if (!questionIdSet.has(key) && !pendingKeySet.has(key)) {
          delete merged[key]
        }
      })

      Object.entries(nextInputs).forEach(([key, value]) => {
        if (pendingKeySet.has(key)) {
          return
        }
        merged[key] = value
      })

      return merged
    })

    setPendingAnswerKeyUpdates((prev) => {
      const entries = Object.entries(prev || {})
      const filteredEntries = entries.filter(([key]) => questionIdSet.has(key) || pendingKeySet.has(key))

      if (filteredEntries.length === entries.length) {
        pendingAnswerKeyUpdatesRef.current = prev || {}
        return prev
      }

      const nextPending = filteredEntries.reduce((acc, [key, value]) => {
        acc[key] = value
        return acc
      }, {})

      pendingAnswerKeyUpdatesRef.current = nextPending
      return nextPending
    })
  }, [booklets, questions, questionFallbackAcceptedOptions])

  useEffect(() => {
    answerKeyStateRef.current = answerKeyState
  }, [answerKeyState])

  useEffect(() => {
    pendingAnswerKeyUpdatesRef.current = pendingAnswerKeyUpdates
  }, [pendingAnswerKeyUpdates])

  useEffect(() => {
    penaltyStateRef.current = penaltyState
  }, [penaltyState])

  useEffect(() => {
    penaltyDirtyRef.current = penaltyDirty
  }, [penaltyDirty])

  useEffect(() => {
    cancellationDirtyRef.current = cancellationDirty
  }, [cancellationDirty])

  useEffect(() => {
    return () => {
      if (answerKeySyncTimeoutRef.current) {
        clearTimeout(answerKeySyncTimeoutRef.current)
      }
    }
  }, [])

  const syncAnswerKeyUpdates = useCallback(async () => {
    if (answerKeySyncTimeoutRef.current) {
      clearTimeout(answerKeySyncTimeoutRef.current)
      answerKeySyncTimeoutRef.current = null
    }
    const examId = Number(exam?.id)
    if (!Number.isFinite(examId)) {
      return
    }

    const pendingEntries = Object.entries(pendingAnswerKeyUpdatesRef.current || {})
    const hasPendingQuestions = pendingEntries.length > 0
    const shouldSyncPenalty = Boolean(penaltyDirtyRef.current || cancellationDirtyRef.current)

    if (!hasPendingQuestions && !shouldSyncPenalty) {
      return
    }

    setAnswerKeySyncing(true)

    const processedEntries = pendingEntries
    const isPenaltyOnlyUpdate = processedEntries.length === 0 && shouldSyncPenalty

    try {
      const bookletUpdates = {}

      processedEntries.forEach(([key]) => {
        const questionId = Number(key)
        if (!Number.isFinite(questionId)) {
          return
        }

        const entry = answerKeyStateRef.current[questionId]
        if (!entry) {
          return
        }

        const baseCodes = Array.isArray(entry.codes) && entry.codes.length > 0 ? entry.codes : availableBookletCodes
        const uniqueCodes = Array.from(new Set(['', ...baseCodes]))

        uniqueCodes.forEach((code) => {
          const resolvedCode = typeof code === 'string' ? code : ''
          const options = Array.isArray(entry.booklets?.[resolvedCode])
            ? entry.booklets[resolvedCode]
            : entry.master || []
          const normalizedOptions = normalizeAcceptedOptionsList(options)

          if (!bookletUpdates[resolvedCode]) {
            bookletUpdates[resolvedCode] = []
          }

          bookletUpdates[resolvedCode].push({
            examQuestionId: questionId,
            acceptedOptions: normalizedOptions,
          })
        })
      })

      const payloadBooklets = Object.entries(bookletUpdates)
        .map(([code, answers]) => ({
          code: code || null,
          answers,
        }))
        .filter((item) => item.answers.length > 0)

      const currentPenalty = penaltyStateRef.current
      const payloadPenalty = {
        applyWrongPenalty: Boolean(currentPenalty.applyPenalty),
        wrongCountForPenalty: Math.max(1, Number(currentPenalty.wrongCount ?? 1)),
        correctCountToSubtract: Math.max(1, Number(currentPenalty.correctDeduction ?? 1)),
      }

      const cancelledQuestions = selectedCancelledQuestionIdsRef.current || []
      const recoveredQuestions = selectedRecoveredQuestionIdsRef.current || []

      payloadPenalty.cancelledQuestionIds = cancelledQuestions
      payloadPenalty.recoveredQuestionIds = recoveredQuestions

      if (payloadBooklets.length === 0 && !shouldSyncPenalty) {
        setAnswerKeySyncing(false)
        return
      }

      openAnswerKeySyncModal()

      const requestPayload = {
        examId,
        booklets: payloadBooklets,
      }

      if (shouldSyncPenalty) {
        requestPayload.penaltyOptions = payloadPenalty
      }

      const response = await updateExamAnswerKey(requestPayload)

      if (processedEntries.length > 0) {
        setPendingAnswerKeyUpdates((prev) => {
          const next = {...prev}
          processedEntries.forEach(([questionId, version]) => {
            if (next[questionId] === version) {
              delete next[questionId]
            }
          })
          pendingAnswerKeyUpdatesRef.current = next
          return next
        })
      }

      if (shouldSyncPenalty) {
        setPenaltyDirty(false)
        penaltyDirtyRef.current = false
        setCancellationDirty(false)
        cancellationDirtyRef.current = false
      }

      if (isPenaltyOnlyUpdate) {
        setData((previous) => {
          if (!previous) {
            return previous
          }

          const existingPenalty = previous.penaltyOptions || {}
          return {
            ...previous,
            penaltyOptions: {
              ...existingPenalty,
              applyWrongPenalty: payloadPenalty.applyWrongPenalty,
              wrongCountForPenalty: payloadPenalty.wrongCountForPenalty,
              correctCountToSubtract: payloadPenalty.correctCountToSubtract,
              cancelledQuestionIds: payloadPenalty.cancelledQuestionIds,
            },
          }
        })
      }

      await loadData({
        silentOverview: true,
        silentQuestions: true,
        studentOptions: buildStudentRequestOptions({silent: true}),
      })

      const responseMessage = typeof response?.message === 'string' ? response.message.trim() : ''
      if (responseMessage) {
        const normalized = responseMessage.toLowerCase()
        const isInfo = normalized.includes('değişiklik') && normalized.includes('bulunamadı')
        Swal.fire({
          icon: isInfo ? 'info' : 'success',
          title: isInfo ? 'Bilgi' : 'Başarılı',
          text: responseMessage,
          timer: 2000,
          showConfirmButton: false,
        })
      } else if (processedEntries.length > 0 || shouldSyncPenalty) {
        showAnswerKeySuccessAlert()
      }
    } catch (err) {
      closeAnswerKeySyncModal()
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Cevap anahtarı güncellenirken bir hata oluştu.'

      Swal.fire({
        title: 'Hata',
        text: message,
        icon: 'error',
        confirmButtonText: 'Tamam',
      })
    } finally {
      closeAnswerKeySyncModal()
      setAnswerKeySyncing(false)
    }
  }, [
    exam?.id,
    availableBookletCodes,
    loadData,
    closeAnswerKeySyncModal,
    openAnswerKeySyncModal,
    showAnswerKeySuccessAlert,
  ])

  const scheduleAnswerKeySync = useCallback((delay = 600) => {
    if (answerKeySyncTimeoutRef.current) {
      clearTimeout(answerKeySyncTimeoutRef.current)
    }

    answerKeySyncTimeoutRef.current = setTimeout(() => {
      syncAnswerKeyUpdates()
    }, delay)
  }, [syncAnswerKeyUpdates])

  const handleToggleQuestionCancellation = useCallback(
    (questionId, nextValue) => {
      const numericQuestionId = Number(questionId)
      if (!Number.isFinite(numericQuestionId)) {
        return
      }

      setQuestionCancellationState((prev) => {
        const next = {...(prev || {})}
        next[numericQuestionId] = Boolean(nextValue)
        questionCancellationStateRef.current = next

        const selectedIds = Object.entries(next)
          .filter(([, value]) => Boolean(value))
          .map(([id]) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0)
        selectedCancelledQuestionIdsRef.current = Array.from(new Set(selectedIds))
        return next
      })

      setCancellationDirty(true)
      cancellationDirtyRef.current = true

      pendingUpdateCounterRef.current += 1
      const version = pendingUpdateCounterRef.current

      setPendingAnswerKeyUpdates((prev) => {
        const next = {...(prev || {})}
        next[numericQuestionId] = version
        pendingAnswerKeyUpdatesRef.current = next
        return next
      })

      scheduleAnswerKeySync()
    },
    [scheduleAnswerKeySync]
  )

  const handleMasterAnswerInputChange = useCallback(
    (questionId, rawValue) => {
      const numericQuestionId = Number(questionId)
      if (!Number.isFinite(numericQuestionId)) {
        return
      }

      const parsedOptions = parseAcceptedOptionsInput(rawValue)
      const normalizedOptions = normalizeAcceptedOptionsList(parsedOptions)
      const formattedValue = formatAcceptedOptionsInput(rawValue, normalizedOptions)

      setAnswerKeyInputs((prev) => ({
        ...prev,
        [numericQuestionId]: formattedValue,
      }))

      const existingEntry = answerKeyStateRef.current[numericQuestionId]
      const existingMaster = Array.isArray(existingEntry?.master) ? existingEntry.master : []
      const normalizedExistingMaster = normalizeAcceptedOptionsList(existingMaster)
      const hasChange =
        normalizedOptions.length !== normalizedExistingMaster.length ||
        normalizedOptions.some((option, index) => option !== normalizedExistingMaster[index])

      if (!hasChange) {
        return
      }

      setAnswerKeyState((prev) => {
        const existing = prev[numericQuestionId] || {master: [], booklets: {}, codes: []}
        const baseCodes = Array.isArray(existing.codes) ? existing.codes : []
        const codeSet = new Set([''])
        baseCodes.forEach((code) => {
          if (typeof code === 'string') {
            codeSet.add(code)
          }
        })
        availableBookletCodes.forEach((code) => {
          if (typeof code === 'string') {
            codeSet.add(code)
          }
        })
        const uniqueCodes = Array.from(codeSet).sort((a, b) => {
          if (a === '' && b !== '') {
            return -1
          }
          if (a !== '' && b === '') {
            return 1
          }
          return (a || '').localeCompare(b || '', 'tr')
        })

        const updatedBooklets = {...existing.booklets}

        uniqueCodes.forEach((rawCode) => {
          const code = typeof rawCode === 'string' ? rawCode : ''
          if (code === '') {
            updatedBooklets[code] = [...normalizedOptions]
            return
          }

          const currentOptions = Array.isArray(updatedBooklets[code]) ? updatedBooklets[code] : []
          const normalizedCurrent = normalizeAcceptedOptionsList(currentOptions)
          const mirrorsMaster =
            normalizedCurrent.length === normalizedExistingMaster.length &&
            normalizedCurrent.every((option, index) => option === normalizedExistingMaster[index])

          if (!Array.isArray(updatedBooklets[code]) || mirrorsMaster) {
            updatedBooklets[code] = [...normalizedOptions]
          } else {
            updatedBooklets[code] = [...normalizedCurrent]
          }
        })

        const nextState = {
          ...prev,
          [numericQuestionId]: {
            master: [...normalizedOptions],
            booklets: updatedBooklets,
            codes: uniqueCodes,
          },
        }

        answerKeyStateRef.current = nextState
        return nextState
      })

      pendingUpdateCounterRef.current += 1
      const version = pendingUpdateCounterRef.current
      setPendingAnswerKeyUpdates((prev) => {
        const next = {
          ...prev,
          [numericQuestionId]: version,
        }
        pendingAnswerKeyUpdatesRef.current = next
        return next
      })

      scheduleAnswerKeySync()
    },
    [availableBookletCodes, scheduleAnswerKeySync]
  )

  const handlePenaltyToggle = useCallback((event) => {
    const checked = Boolean(event?.target?.checked)
    if (penaltyStateRef.current.applyPenalty === checked) {
      return
    }
    setPenaltyState((prev) => {
      const next = {
        ...prev,
        applyPenalty: checked,
      }
      penaltyStateRef.current = next
      return next
    })
    setPenaltyDirty(true)
    penaltyDirtyRef.current = true
    scheduleAnswerKeySync(0)
  }, [scheduleAnswerKeySync])

  const handlePenaltyValueChange = useCallback((key, rawValue) => {
    const numeric = Number(rawValue)
    const sanitized = Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 1
    if (Number(penaltyStateRef.current[key]) === sanitized) {
      return
    }
    setPenaltyState((prev) => {
      const next = {
        ...prev,
        [key]: sanitized,
      }
      penaltyStateRef.current = next
      return next
    })
    setPenaltyDirty(true)
    penaltyDirtyRef.current = true
    scheduleAnswerKeySync(0)
  }, [scheduleAnswerKeySync])
  const openEndedQuestions = useMemo(
    () => questions.filter((question) => !question.isMultipleChoice),
    [questions]
  )
  const multipleChoiceQuestions = useMemo(
    () => questions.filter((question) => question.isMultipleChoice),
    [questions]
  )

  const questionOrderLookup = useMemo(() => {
    const lookup = {}
    questions.forEach((question) => {
      if (typeof question?.id !== 'number') {
        return
      }

      lookup[question.id] = typeof question?.order === 'number' ? question.order : null
    })

    return lookup
  }, [questions])

  const bookletQuestionMap = useMemo(() => {
    const map = {}

    if (Array.isArray(booklets)) {
      booklets.forEach((booklet) => {
        const normalizedCode = normalizeBookletCode(booklet?.code)
        const codeKey = normalizedCode || ''
        if (!map[codeKey]) {
          map[codeKey] = {
            code: codeKey,
            label: resolveBookletLabel(booklet?.code),
            questions: {},
          }
        }

        const questionMap = map[codeKey].questions
        const entries = Array.isArray(booklet?.questions) ? booklet.questions : []
        entries.forEach((entry) => {
          if (typeof entry?.examQuestionId !== 'number') {
            return
          }

          const questionId = entry.examQuestionId
          const acceptedOptions = normalizeAcceptedOptionsList(entry?.acceptedOptions)
          const numericQuestionNumber = Number(entry?.questionNumber)
          const questionNumber =
            Number.isFinite(numericQuestionNumber) && numericQuestionNumber > 0
              ? numericQuestionNumber
              : null

          questionMap[questionId] = {
            questionNumber,
            acceptedOptions,
          }
        })
      })
    }

    if (!map['']) {
      map[''] = {code: '', label: 'Master Kitapçık', questions: {}}
    }

    const masterEntry = map['']
    const allQuestionIds = new Set()

    Object.values(map).forEach((info) => {
      if (!info || !info.questions) {
        return
      }

      Object.keys(info.questions).forEach((questionId) => {
        allQuestionIds.add(Number(questionId))
      })
    })

    Object.keys(questionFallbackAcceptedOptions).forEach((questionId) => {
      allQuestionIds.add(Number(questionId))
    })

    allQuestionIds.forEach((rawId) => {
      const questionId = Number(rawId)
      if (!Number.isFinite(questionId)) {
        return
      }

      const existingMaster = masterEntry.questions[questionId]
      const hasAccepted = Array.isArray(existingMaster?.acceptedOptions)
        ? existingMaster.acceptedOptions.length > 0
        : false

      let accepted = hasAccepted ? existingMaster.acceptedOptions : []

      if (!hasAccepted) {
        const fallbackFromQuestion = questionFallbackAcceptedOptions[questionId]
        if (Array.isArray(fallbackFromQuestion) && fallbackFromQuestion.length > 0) {
          accepted = fallbackFromQuestion
        } else {
          const fallbackFromBooklet = Object.entries(map)
            .filter(([code]) => code !== '')
            .map(([, info]) => info?.questions?.[questionId])
            .find((detail) => Array.isArray(detail?.acceptedOptions) && detail.acceptedOptions.length > 0)
          if (fallbackFromBooklet && fallbackFromBooklet.acceptedOptions) {
            accepted = fallbackFromBooklet.acceptedOptions
          }
        }
      }

      let questionNumber =
        typeof existingMaster?.questionNumber === 'number' && existingMaster.questionNumber > 0
          ? existingMaster.questionNumber
          : null
      if (questionNumber === null) {
        const fallbackNumber = Object.entries(map)
          .map(([, info]) => info?.questions?.[questionId])
          .find((detail) => typeof detail?.questionNumber === 'number' && detail.questionNumber > 0)?.questionNumber

        if (typeof fallbackNumber === 'number' && fallbackNumber > 0) {
          questionNumber = fallbackNumber
        } else {
          const lookupNumber = questionOrderLookup[questionId]
          questionNumber = typeof lookupNumber === 'number' ? lookupNumber : null
        }
      }

      masterEntry.questions[questionId] = {
        questionNumber,
        acceptedOptions: normalizeAcceptedOptionsList(accepted),
      }
    })

    return map
  }, [booklets, questionFallbackAcceptedOptions, questionOrderLookup])

  useEffect(() => {
    if (!students.length) {
      setScores((prev) => {
        if (Object.keys(prev).length === 0) {
          return prev
        }

        return {}
      })
      return
    }

    const initialScores = {}
    students.forEach((student) => {
      const studentId = student.studentSemesterCourseId
      const resultMap = {}
      if (Array.isArray(student.examResults)) {
        student.examResults.forEach((result) => {
          if (typeof result?.examQuestionId !== 'number') {
            return
          }

          const normalizedPoint = parseNumericValue(result?.point)
          if (typeof normalizedPoint === 'number') {
            resultMap[result.examQuestionId] = normalizedPoint
          }
        })
      }

      initialScores[studentId] = {}
      openEndedQuestions.forEach((question) => {
        const existing = resultMap[question.id]
        initialScores[studentId][question.id] = typeof existing === 'number' ? Number(existing) : ''
      })
    })

    setScores(initialScores)
  }, [students, openEndedQuestions])

  const questionBookletOrders = useMemo(() => {
    const mapping = {}
    booklets.forEach((booklet) => {
      const normalizedCode = normalizeBookletCode(booklet?.code)
      const label = resolveBookletLabel(booklet?.code)
      if (Array.isArray(booklet?.questions)) {
        booklet.questions.forEach((question) => {
          const questionNumber = Number(question?.questionNumber)
          if (
            typeof question?.examQuestionId !== 'number' ||
            !Number.isFinite(questionNumber) ||
            questionNumber <= 0
          ) {
            return
          }

          if (!mapping[question.examQuestionId]) {
            mapping[question.examQuestionId] = []
          }

          mapping[question.examQuestionId].push({
            code: normalizedCode || '',
            label,
            questionNumber,
          })
        })
      }
    })

    Object.keys(mapping).forEach((key) => {
      mapping[key].sort((a, b) => {
        if (a.code === '' && b.code !== '') {
          return -1
        }
        if (a.code !== '' && b.code === '') {
          return 1
        }
        return a.label.localeCompare(b.label, 'tr')
      })
    })

    return mapping
  }, [booklets])

  const statusCounts = useMemo(() => {
    if (!summary) {
      return null
    }

    return {
      Matched: summary.matchedEntries ?? 0,
      Duplicate: summary.duplicateEntries ?? 0,
      Unregistered: summary.unregisteredEntries ?? 0,
      BookletMismatch: summary.bookletMismatchEntries ?? 0,
      Ambiguous: summary.ambiguousEntries ?? 0,
      Skipped: summary.skippedEntries ?? 0,
      InvalidLine: summary.invalidEntries ?? 0,
    }
  }, [summary])

  const missingStudentCount = useMemo(() => {
    if (!summary) {
      return 0
    }
    const expected = typeof exam?.expectedStudentCount === 'number' ? exam.expectedStudentCount : students.length
    const matched = summary.matchedEntries ?? 0
    return expected > matched ? expected - matched : 0
  }, [summary, exam, students.length])

  const hasQueuedAnswerKeyUpdates = useMemo(
    () => penaltyDirty || Object.keys(pendingAnswerKeyUpdates).length > 0,
    [pendingAnswerKeyUpdates, penaltyDirty]
  )

  const statusValue = typeof exam?.status === 'number' ? exam.status : null
  const normalizedStatusName = typeof exam?.statusName === 'string' ? exam.statusName.trim().toLowerCase() : ''
  const isOpticalCompleted =
    statusValue === 6 || normalizedStatusName === 'opticalreadingcompleted' || normalizedStatusName === 'gradingcompleted'
  const isExamCompleted = statusValue !== null ? statusValue >= 4 : ['examcompleted', 'gradingcompleted', 'opticalreadingcompleted'].includes(normalizedStatusName)
  const hasMultipleChoiceQuestions = Boolean(exam?.hasMultipleChoiceQuestions)
  const canEditAnswerKey = Boolean(summary && hasMultipleChoiceQuestions)
  const isPageActive = isOpticalCompleted || (!hasMultipleChoiceQuestions && isExamCompleted)

  const disableReason = useMemo(() => {
    if (!exam || isPageActive) {
      return null
    }

    if (hasMultipleChoiceQuestions) {
      return 'Optik okuma tamamlanmadan puan girişi yapılamaz.'
    }

    return 'Sınav durumu "Sınav Tamamlandı" değilken açık uçlu sorular için puan girişi yapılamaz.'
  }, [exam, isPageActive, hasMultipleChoiceQuestions])

  const getStudentScore = useCallback(
    (studentId, questionId) => {
      const studentScores = scores[studentId]
      if (studentScores && typeof studentScores[questionId] === 'number') {
        return studentScores[questionId]
      }

      return ''
    },
    [scores]
  )

  const handleScoreChange = useCallback(
    (studentId, questionId, rawValue) => {
      const question = openEndedQuestions.find((item) => item.id === questionId)
      const maxValue = typeof question?.point === 'number' ? Number(question.point) : null
      const sanitized = clampScore(rawValue, maxValue)

      setScores((prev) => {
        const current = {...prev}
        const studentScores = {...(current[studentId] || {})}
        studentScores[questionId] = sanitized
        current[studentId] = studentScores
        return current
      })
    },
    [openEndedQuestions]
  )

  const handleSendGradesToObs = useCallback(async () => {
    if (!Number.isFinite(normalizedExamId)) {
      return
    }

    if (!obsTransfer?.isObsEnabled) {
      Swal.fire({
        icon: 'info',
        title: 'OBS entegrasyonu pasif',
        text: obsDisabledReason || 'OBS entegrasyonu etkin değil.',
      })
      return
    }

    if (requiresObsTargetSelection && obsTargetOptions.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'OBS sınav seçimi',
        text: 'OBS sınav seçenekleri yüklenemedi.',
      })
      return
    }

    let targetCodeToSend = null

    if (obsTargetOptions.length > 1) {
      const inputOptions = obsTargetOptions.reduce((acc, option) => {
        acc[option.code] = option.label
        return acc
      }, {})

      const {value, isConfirmed} = await Swal.fire({
        title: 'OBS sınav seçimi',
        input: 'select',
        inputLabel: 'OBS üzerinde hangi sınav güncellenecek?',
        inputPlaceholder: 'Sınav seçiniz',
        inputOptions,
        showCancelButton: true,
        confirmButtonText: 'Gönder',
        cancelButtonText: 'Vazgeç',
      })

      if (!isConfirmed) {
        return
      }

      const selected = typeof value === 'string' ? value.trim() : ''
      if (!selected) {
        return
      }

      targetCodeToSend = selected
    } else if (obsTargetOptions.length === 1) {
      targetCodeToSend = obsTargetOptions[0].code
    }

    setObsSending(true)

    try {
      const response = await sendExamGradesToObs(
        Number(normalizedExamId),
        targetCodeToSend || undefined
      )
      const message =
        typeof response?.message === 'string' && response.message.trim().length > 0
          ? response.message.trim()
          : 'OBS gönderim isteği oluşturuldu.'

      Swal.fire({
        icon: 'success',
        title: 'OBS gönderimi',
        text: message,
      })
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        'OBS gönderimi gerçekleştirilemedi.'
      const normalizedMessage =
        typeof message === 'string' && message.trim().length > 0
          ? message.trim()
          : 'OBS gönderimi gerçekleştirilemedi.'

      Swal.fire({
        icon: 'error',
        title: 'OBS gönderimi başarısız',
        text: normalizedMessage,
      })
    } finally {
      setObsSending(false)
    }
  }, [
    normalizedExamId,
    obsTransfer,
    obsDisabledReason,
    obsTargetOptions,
    requiresObsTargetSelection,
  ])

  const openQuestionPreview = useCallback((examQuestionId, questionNumber = null) => {
    if (typeof examQuestionId !== 'number') {
      return
    }

    setQuestionPreviewContext({
      examQuestionId,
      questionNumber: typeof questionNumber === 'number' ? questionNumber : null,
    })
  }, [])

  const closeQuestionPreview = useCallback(() => {
    setQuestionPreviewContext(null)
  }, [])

  const getStudentMultipleChoiceScore = useCallback(
    (student) => {
      const studentResults = studentResultLookup[student?.studentSemesterCourseId] || {}
      const aggregated = questions.reduce((total, question) => {
        if (!question.isMultipleChoice) {
          return total
        }

        if (questionCancellationState?.[question.id]) {
          return total
        }

        const existing = studentResults[question.id]
        const point = normalizeScoreValue(existing?.point) ?? 0
        return total + point
      }, 0)

      if (aggregated !== 0) {
        return aggregated
      }

      const directScore = [
        student?.totalScore,
        student?.multipleChoiceScore,
        student?.multipleChoicePoint,
        student?.multipleChoiceTotal,
        student?.opticalScore,
        student?.opticalPoint,
      ]
        .map((value) => normalizeScoreValue(value))
        .find((value) => value !== null)

      return directScore ?? 0
    },
    [questionCancellationState, questions, studentResultLookup]
  )

  const resolveCorrectOptionsForBooklet = useCallback(
    (question, normalizedBookletCode) => {
      const answerEntry = answerKeyState[question.id] || null

      if (answerEntry && typeof answerEntry === 'object') {
        const bookletEntries =
          answerEntry.booklets && typeof answerEntry.booklets === 'object' ? answerEntry.booklets : null

        if (bookletEntries) {
          const bookletCodesToTry = normalizedBookletCode ? [normalizedBookletCode, ''] : ['']
          if (!bookletCodesToTry.includes('MASTER')) {
            bookletCodesToTry.push('MASTER')
          }

          for (const code of bookletCodesToTry) {
            const options = bookletEntries[code]
            if (Array.isArray(options) && options.length > 0) {
              return normalizeAcceptedOptionsList(options)
            }
          }
        }

        if (Array.isArray(answerEntry.master) && answerEntry.master.length > 0) {
          return normalizeAcceptedOptionsList(answerEntry.master)
        }
      }

      const bookletInfo = bookletQuestionMap[normalizedBookletCode]?.questions?.[question.id]
      if (bookletInfo && Array.isArray(bookletInfo.acceptedOptions) && bookletInfo.acceptedOptions.length > 0) {
        return normalizeAcceptedOptionsList(bookletInfo.acceptedOptions)
      }

      const masterInfo = bookletQuestionMap['']?.questions?.[question.id]
      if (masterInfo && Array.isArray(masterInfo.acceptedOptions) && masterInfo.acceptedOptions.length > 0) {
        return normalizeAcceptedOptionsList(masterInfo.acceptedOptions)
      }

      const fallbackFromQuestion = questionFallbackAcceptedOptions[question.id]
      if (Array.isArray(fallbackFromQuestion) && fallbackFromQuestion.length > 0) {
        return normalizeAcceptedOptionsList(fallbackFromQuestion)
      }

      const optionItems = Array.isArray(question?.questionItems) ? question.questionItems : []
      if (optionItems.length > 0) {
        return optionItems.reduce((acc, item, index) => {
          if (!item?.isTrue) {
            return acc
          }

          const label = resolveQuestionItemLabel(item, index)
          if (label && !acc.includes(label)) {
            acc.push(label)
          }

          return acc
        }, [])
      }

      return []
    },
    [answerKeyState, bookletQuestionMap, questionFallbackAcceptedOptions]
  )

  const resolveSelectedOptionsForBooklet = useCallback(
    (question, studentAnswer, normalizedBookletCode) => {
      const selectedOptionTokens = Array.isArray(studentAnswer?.selectedOptionIds)
        ? studentAnswer.selectedOptionIds.map((value) => normalizeOptionIdentifier(value)).filter((value) => Boolean(value))
        : []
      const selectedOptionTokenSet = selectedOptionTokens.length > 0 ? new Set(selectedOptionTokens) : null
      const selectedPositionOrders = selectedOptionTokens.reduce((set, token) => {
        const numeric = Number(token)
        if (Number.isFinite(numeric)) {
          set.add(numeric)
        }

        const letterOrder = mapLetterToOrder(token)
        if (letterOrder !== null) {
          set.add(letterOrder)
        }

        return set
      }, new Set())
      const selectedPositionSet = selectedPositionOrders.size > 0 ? selectedPositionOrders : null

      const optionOrderMap =
        question && typeof question.optionOrderByBooklet === 'object' && question.optionOrderByBooklet !== null
          ? question.optionOrderByBooklet
          : {}

      let orderedQuestionItems = Array.isArray(question.questionItems) ? [...question.questionItems] : []
      let variantOrderApplied = false
      let optionLetterOverrideMap = null

      if (orderedQuestionItems.length > 0) {
        const variantOrder = (() => {
          if (
            normalizedBookletCode &&
            Array.isArray(optionOrderMap[normalizedBookletCode]) &&
            optionOrderMap[normalizedBookletCode].length > 0
          ) {
            return optionOrderMap[normalizedBookletCode]
          }

          if (Array.isArray(optionOrderMap['']) && optionOrderMap[''].length > 0) {
            return optionOrderMap['']
          }

          if (Array.isArray(optionOrderMap.MASTER) && optionOrderMap.MASTER.length > 0) {
            return optionOrderMap.MASTER
          }

          return null
        })()

        if (variantOrder) {
          variantOrderApplied = Array.isArray(variantOrder) && variantOrder.length > 0
          const itemsById = new Map()
          orderedQuestionItems.forEach((item) => {
            if (item && item.id !== undefined && item.id !== null) {
              const numericId = Number(item.id)
              if (Number.isFinite(numericId)) {
                itemsById.set(numericId, item)
              }
            }
          })

          const rearranged = []
          variantOrder.forEach((optionId) => {
            const numericId = Number(optionId)
            if (Number.isFinite(numericId) && itemsById.has(numericId)) {
              const mappedItem = itemsById.get(numericId)
              if (mappedItem && !rearranged.includes(mappedItem)) {
                rearranged.push(mappedItem)
              }
            }
          })

          orderedQuestionItems.forEach((item) => {
            const numericId = Number(item?.id)
            if (!Number.isFinite(numericId)) {
              rearranged.push(item)
              return
            }

            if (!rearranged.some((existing) => Number(existing?.id) === numericId)) {
              rearranged.push(item)
            }
          })

          orderedQuestionItems = rearranged
        }
      }

      if (variantOrderApplied) {
        const overrideMap = new Map()
        orderedQuestionItems.forEach((item, index) => {
          const numericId = Number(item?.id)
          if (!Number.isFinite(numericId)) {
            return
          }

          const letter = mapOrderToLetter(index + 1)
          if (letter) {
            overrideMap.set(numericId, letter)
          }
        })

        if (overrideMap.size > 0) {
          optionLetterOverrideMap = overrideMap
        }
      }

      let selectedOptionLabels = []
      if (orderedQuestionItems.length > 0) {
        if (selectedOptionTokenSet) {
          selectedOptionLabels = orderedQuestionItems.reduce((acc, item, index) => {
            const normalizedItemId = normalizeOptionIdentifier(item?.id)
            const normalizedOrder = normalizeOptionIdentifier(item?.order)
            const normalizedKey = normalizeOptionIdentifier(item?.key)
            const resolvedLabel = resolveQuestionItemLabel(item, index)
            const normalizedResolvedLabel = normalizeOptionIdentifier(resolvedLabel)

            const numericItemId = Number(item?.id)
            const overrideLabel =
              Number.isFinite(numericItemId) && optionLetterOverrideMap ? optionLetterOverrideMap.get(numericItemId) : null
            const normalizedOverride = normalizeOptionIdentifier(overrideLabel)

            const positionMatch = selectedPositionSet ? selectedPositionSet.has(index + 1) : false

            const identifierTokens = [normalizedItemId, normalizedKey]
            if (!selectedPositionSet) {
              identifierTokens.push(normalizedOrder)
            }

            const matchesByToken = identifierTokens
              .filter(Boolean)
              .some((identifier) => selectedOptionTokenSet.has(identifier))

            let matchesByLabelToken = false
            if (selectedOptionTokenSet && selectedOptionTokenSet.size > 0) {
              if (optionLetterOverrideMap && optionLetterOverrideMap.size > 0 && normalizedOverride) {
                matchesByLabelToken = selectedOptionTokenSet.has(normalizedOverride)
              } else if (!variantOrderApplied && normalizedResolvedLabel) {
                matchesByLabelToken = selectedOptionTokenSet.has(normalizedResolvedLabel)
              }
            }

            const matches = positionMatch || matchesByToken || matchesByLabelToken

            if (!matches) {
              return acc
            }

            const displayLabel = overrideLabel || resolvedLabel || mapOrderToLetter(index + 1) || String(index + 1)

            if (displayLabel) {
              acc.push(displayLabel)
            }

            return acc
          }, [])
        }

        if (selectedOptionLabels.length === 0 && question.isMultipleChoice) {
          selectedOptionLabels = extractOptionLabelsFromAnswerText(studentAnswer?.answerText || '')
        }
      } else if (question.isMultipleChoice) {
        selectedOptionLabels = extractOptionLabelsFromAnswerText(studentAnswer?.answerText || '')
      }

      if (selectedOptionLabels.length > 0) {
        selectedOptionLabels = normalizeAcceptedOptionsList(selectedOptionLabels)
      }

      return selectedOptionLabels
    },
    []
  )

  const calculateMultipleChoiceScoreForBooklet = useCallback(
    (student, normalizedBookletCode) => {
      const studentAnswers = normalizeStudentAnswers(student)
      const normalizedCode = normalizeBookletCode(normalizedBookletCode)

      const aggregation = multipleChoiceQuestions.reduce(
        (acc, question) => {
          const studentAnswer = studentAnswers.find((answer) => answer?.examQuestionId === question.id)

          if (!studentAnswer) {
            return acc
          }

          const selectedOptions = resolveSelectedOptionsForBooklet(question, studentAnswer, normalizedCode)
          const correctOptions = resolveCorrectOptionsForBooklet(question, normalizedCode)

          if (selectedOptions.length === 0 || correctOptions.length === 0) {
            return acc
          }

          const selectedSet = new Set(selectedOptions)
          const correctSet = new Set(correctOptions)
          const isCorrect =
            correctSet.size === selectedSet.size && Array.from(correctSet).every((option) => selectedSet.has(option))

          if (isCorrect) {
            const questionPoint = normalizeScoreValue(question.point) ?? 0
            return {...acc, total: acc.total + questionPoint}
          }

          return {...acc, wrongCount: acc.wrongCount + 1}
        },
        {total: 0, wrongCount: 0}
      )

      const currentPenalty = penaltyStateRef.current
      if (!currentPenalty?.applyPenalty) {
        return aggregation.total
      }

      const wrongCountForPenalty = Math.max(1, Number(currentPenalty.wrongCount ?? 1))
      const correctDeduction = Math.max(0, Number(currentPenalty.correctDeduction ?? 0))
      if (correctDeduction === 0) {
        return aggregation.total
      }

      const penaltySteps = Math.floor(aggregation.wrongCount / wrongCountForPenalty) * correctDeduction
      if (penaltySteps === 0) {
        return aggregation.total
      }

      const totalQuestionPoints = multipleChoiceQuestions.reduce(
        (sum, question) => sum + (normalizeScoreValue(question.point) ?? 0),
        0
      )
      const averageQuestionPoint = multipleChoiceQuestions.length > 0 ? totalQuestionPoints / multipleChoiceQuestions.length : 0
      const penaltyPoints = penaltySteps * averageQuestionPoint
      const penalizedTotal = aggregation.total - penaltyPoints

      return Math.max(0, penalizedTotal)
    },
    [
      multipleChoiceQuestions,
      resolveCorrectOptionsForBooklet,
      resolveSelectedOptionsForBooklet,
    ]
  )

  const getStudentTotal = useCallback(
    (student) => {
      const studentResults = studentResultLookup[student?.studentSemesterCourseId] || {}

      let hasManualOverride = false
      const openEndedTotal = openEndedQuestions.reduce((total, question) => {
        if (questionCancellationState?.[question.id]) {
          return total
        }

        const point = getStudentScore(student.studentSemesterCourseId, question.id)
        const normalizedPoint = normalizeScoreValue(point)
        const existingPoint = normalizeScoreValue(studentResults[question.id]?.point)

        if (normalizedPoint !== null && normalizedPoint !== existingPoint) {
          hasManualOverride = true
        }

        return total + (normalizedPoint ?? 0)
      }, 0)

      const baseOpenEndedTotal = openEndedQuestions.reduce((total, question) => {
        if (questionCancellationState?.[question.id]) {
          return total
        }

        const existingPoint = normalizeScoreValue(studentResults[question.id]?.point) ?? 0
        return total + existingPoint
      }, 0)

      let multipleChoiceTotal = 0
      let hasMultipleChoiceScore = false

      multipleChoiceQuestions.forEach((question) => {
        if (questionCancellationState?.[question.id]) {
          return
        }

        const existing = studentResults[question.id]
        const point = normalizeScoreValue(existing?.point)
        if (point !== null) {
          hasMultipleChoiceScore = true
          multipleChoiceTotal += point
        }
      })

      if (!hasMultipleChoiceScore) {
        const fallbackMultipleChoice = getStudentMultipleChoiceScore(student)
        multipleChoiceTotal = typeof fallbackMultipleChoice === 'number' ? fallbackMultipleChoice : 0
      }

      const directTotal = normalizeScoreValue(student?.totalScore)

      const baseAggregated = baseOpenEndedTotal + multipleChoiceTotal
      const aggregated = openEndedTotal + multipleChoiceTotal

      if (typeof directTotal === 'number' && !hasManualOverride) {
        return directTotal
      }

      if (typeof directTotal === 'number' && baseAggregated > 0 && hasManualOverride) {
        const scaleFactor = directTotal / baseAggregated
        return aggregated * scaleFactor
      }

      return directTotal ?? aggregated
    },
    [
      getStudentMultipleChoiceScore,
      getStudentScore,
      multipleChoiceQuestions,
      openEndedQuestions,
      questionCancellationState,
      studentResultLookup,
    ]
  )

  const openBookletEvaluationModal = useCallback(
    async (student) => {
      if (!student || !Number.isFinite(normalizedExamId)) {
        return
      }

      const openEndedScore = openEndedQuestions.reduce((total, question) => {
        const point = getStudentScore(student.studentSemesterCourseId, question.id)
        return total + point
      }, 0)

      setBookletEvaluationApplying(null)
      setBookletEvaluationLoading(true)
      setBookletEvaluationContext({student, evaluations: [], openEndedScore})

      try {
        const response = await evaluateStudentBookletCodes(
          Number(normalizedExamId),
          student.studentSemesterCourseId
        )

        const rawEvaluations = Array.isArray(response?.evaluations) ? response.evaluations : []
        const normalizedEvaluations = rawEvaluations
          .map((entry) => {
            const normalizedCode = normalizeBookletCode(entry?.bookletCode)
            const score =
              entry && typeof entry.multipleChoiceScore === 'number'
                ? entry.multipleChoiceScore
                : Number(entry?.multipleChoiceScore) || 0
            return {
              code: normalizedCode,
              label: resolveBookletLabel(normalizedCode),
              multipleChoiceScore: score,
              totalScore: openEndedScore + score,
            }
          })
          .filter((entry) => typeof entry.code === 'string' && entry.code.length > 0)

        setBookletEvaluationContext({
          student,
          evaluations: normalizedEvaluations,
          openEndedScore,
        })
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Kitapçık kodu değerlendirmesi alınamadı.'

        Swal.fire({icon: 'error', title: 'Hata', text: message})
        setBookletEvaluationContext(null)
      } finally {
        setBookletEvaluationLoading(false)
      }
    },
    [
      evaluateStudentBookletCodes,
      getStudentScore,
      normalizedExamId,
      openEndedQuestions,
      resolveBookletLabel,
    ]
  )

  const closeBookletEvaluationModal = useCallback(() => {
    setBookletEvaluationContext(null)
    setBookletEvaluationLoading(false)
    setBookletEvaluationApplying(null)
  }, [])

  const applyBookletEvaluation = useCallback(
    async (bookletCode) => {
      if (!bookletEvaluationContext?.student || !Number.isFinite(normalizedExamId)) {
        return
      }

      const studentId = bookletEvaluationContext.student.studentSemesterCourseId
      if (!studentId) {
        return
      }

      setBookletEvaluationApplying(typeof bookletCode === 'string' ? bookletCode : '')

      try {
        await applyStudentBookletEvaluation(Number(normalizedExamId), studentId, bookletCode)
        Swal.fire({icon: 'success', title: 'Başarılı', text: 'Öğrenci puanı güncellendi.'})
        await loadStudentData({silent: true})
        closeBookletEvaluationModal()
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Kitapçık değerlendirmesi uygulanamadı.'

        Swal.fire({icon: 'error', title: 'Hata', text: message})
      } finally {
        setBookletEvaluationApplying(null)
      }
    },
    [bookletEvaluationContext?.student, closeBookletEvaluationModal, loadStudentData, normalizedExamId]
  )

  const studentTableRows = useMemo(() => {
    return students.map((student) => {
      const fullName = `${student.name ?? ''} ${student.surname ?? ''}`.trim()
      const numberValue =
        typeof student.number === 'string'
          ? student.number
          : Number.isFinite(student.number)
          ? String(student.number)
          : ''

      return {
        ...student,
        fullName,
        numberValue,
        totalScore: getStudentTotal(student),
        facultyDisplay: resolveStudentFaculty(student),
        departmentDisplay: resolveStudentDepartment(student),
      }
    })
  }, [students, getStudentTotal])

  const studentTotalScores = useMemo(() => {
    return studentTableRows
      .map((row) => row.totalScore)
      .filter((value) => typeof value === 'number' && Number.isFinite(value))
  }, [studentTableRows])

  const scoreExtremes = useMemo(() => {
    if (studentTotalScores.length === 0) {
      return {min: null, max: null}
    }

    return {
      min: Math.min(...studentTotalScores),
      max: Math.max(...studentTotalScores),
    }
  }, [studentTotalScores])

  const descriptiveStats = useMemo(() => {
    if (studentTotalScores.length === 0) {
      return {average: null, median: null}
    }

    const sortedScores = [...studentTotalScores].sort((a, b) => a - b)
    const middleIndex = Math.floor(sortedScores.length / 2)
    const median =
      sortedScores.length % 2 === 1
        ? sortedScores[middleIndex]
        : (sortedScores[middleIndex - 1] + sortedScores[middleIndex]) / 2

    const sum = studentTotalScores.reduce((total, score) => total + score, 0)
    const average = sum / studentTotalScores.length

    return {average, median}
  }, [studentTotalScores])

  const canUseLocalDescriptiveStats = useMemo(() => {
    if (!useServerPaging) {
      return true
    }

    if (studentTotalScores.length === 0) {
      return false
    }

    if (typeof remoteTotalCount === 'number' && remoteTotalCount <= studentTotalScores.length) {
      return true
    }

    return false
  }, [remoteTotalCount, studentTotalScores.length, useServerPaging])

  const averageScore = useMemo(() => {
    if (
      canUseLocalDescriptiveStats &&
      typeof descriptiveStats.average === 'number' &&
      Number.isFinite(descriptiveStats.average)
    ) {
      return descriptiveStats.average
    }

    const candidate = [summary?.averageScore, summary?.avgScore, summary?.meanScore].find(
      (value) => typeof value === 'number' && Number.isFinite(value)
    )

    return typeof candidate === 'number' ? candidate : null
  }, [canUseLocalDescriptiveStats, descriptiveStats.average, summary])

  const medianScore = useMemo(() => {
    if (
      canUseLocalDescriptiveStats &&
      typeof descriptiveStats.median === 'number' &&
      Number.isFinite(descriptiveStats.median)
    ) {
      return descriptiveStats.median
    }

    const candidate = [summary?.medianScore].find((value) => typeof value === 'number' && Number.isFinite(value))

    return typeof candidate === 'number' ? candidate : null
  }, [canUseLocalDescriptiveStats, descriptiveStats.median, summary])

  const minimumScore = useMemo(() => {
    const candidate = [summary?.minimumScore, summary?.minScore].find(
      (value) => typeof value === 'number' && Number.isFinite(value)
    )

    if (typeof candidate === 'number') {
      return candidate
    }

    return scoreExtremes.min
  }, [scoreExtremes.min, summary])

  const maximumScore = useMemo(() => {
    const candidate = [summary?.maximumScore, summary?.maxScore].find(
      (value) => typeof value === 'number' && Number.isFinite(value)
    )

    if (typeof candidate === 'number') {
      return candidate
    }

    return scoreExtremes.max
  }, [scoreExtremes.max, summary])

  const filteredStudents = useMemo(() => {
    if (useServerPaging) {
      return studentTableRows
    }

    const normalizedNameFilter = normalizeText(appliedNameFilter)
    const normalizedNumberFilter = normalizeText(appliedNumberFilter)

    return studentTableRows.filter((row) => {
      const matchesName = normalizedNameFilter
        ? normalizeText(row.fullName).includes(normalizedNameFilter)
        : true
      const matchesNumber = normalizedNumberFilter
        ? normalizeText(row.numberValue).includes(normalizedNumberFilter)
        : true

      return matchesName && matchesNumber
    })
  }, [appliedNameFilter, appliedNumberFilter, studentTableRows, useServerPaging])

  const sortedStudents = useMemo(() => {
    if (useServerPaging) {
      return studentTableRows
    }

    const sorted = [...filteredStudents]
    const directionFactor = sortDirection === 'desc' ? -1 : 1

    sorted.sort((a, b) => {
      if (sortField === 'number') {
        return directionFactor * (a.numberValue || '').localeCompare(b.numberValue || '', 'tr', {sensitivity: 'base'})
      }

      if (sortField === 'total') {
        const valueA = typeof a.totalScore === 'number' ? a.totalScore : -Infinity
        const valueB = typeof b.totalScore === 'number' ? b.totalScore : -Infinity
        if (valueA !== valueB) {
          return directionFactor * (valueA - valueB)
        }
        return directionFactor * (a.fullName || '').localeCompare(b.fullName || '', 'tr', {sensitivity: 'base'})
      }

      return directionFactor * (a.fullName || '').localeCompare(b.fullName || '', 'tr', {sensitivity: 'base'})
    })

    return sorted
  }, [filteredStudents, sortDirection, sortField, studentTableRows, useServerPaging])

  const totalPages = useMemo(() => {
    if (totalStudentCount === 0) {
      return 1
    }

    const divisor = effectivePageSize > 0 ? effectivePageSize : 1
    const sourceCount = useServerPaging ? totalStudentCount : sortedStudents.length
    return Math.max(1, Math.ceil(sourceCount / divisor))
  }, [effectivePageSize, sortedStudents.length, totalStudentCount, useServerPaging])

  useEffect(() => {
    setPageNumber((previous) => {
      const maxPage = Math.max(totalPages, 1)
      const next = Math.min(Math.max(previous, 1), maxPage)
      return next
    })
  }, [totalPages])

  const paginatedStudents = useMemo(() => {
    if (useServerPaging) {
      return sortedStudents
    }

    const startIndex = (pageNumber - 1) * effectivePageSize
    const endIndex = startIndex + effectivePageSize
    return sortedStudents.slice(startIndex, endIndex)
  }, [effectivePageSize, pageNumber, sortedStudents, useServerPaging])

  const downloadStudentExcel = useCallback(async () => {
    let exportStudents = studentTableRows

    if (useServerPaging) {
      if (!Number.isFinite(normalizedExamId) || !Number.isFinite(totalStudentCount) || totalStudentCount <= 0) {
        return
      }

      try {
        const response = await getExamResultStudents(normalizedExamId, {
          pageNumber: 1,
          pageSize: totalStudentCount,
          sortField,
          sortDirection,
        })
        const source = response?.students ?? response
        let responseStudents = []
        if (Array.isArray(source?.students)) {
          responseStudents = source.students
        } else if (Array.isArray(source?.students?.items)) {
          responseStudents = source.students.items
        } else if (Array.isArray(source?.students?.data)) {
          responseStudents = source.students.data
        } else if (Array.isArray(source?.items)) {
          responseStudents = source.items
        } else if (Array.isArray(source)) {
          responseStudents = source
        }

        exportStudents = responseStudents.map((student) => {
          const fullName = `${student.name ?? ''} ${student.surname ?? ''}`.trim()
          const numberValue =
            typeof student.number === 'string'
              ? student.number
              : Number.isFinite(student.number)
              ? String(student.number)
              : ''

          return {
            ...student,
            fullName,
            numberValue,
            totalScore: getStudentTotal(student),
          }
        })
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          'Öğrenci listesi indirilemedi.'
        Swal.fire({icon: 'error', title: 'Hata', text: message})
        return
      }
    }

    if (!exportStudents.length) {
      return
    }

    const header = ['Öğrenci Adı Soyadı', 'Öğrenci No', 'Toplam Not']
    const body = exportStudents.map((student) => [
      student.fullName || `${student.name ?? ''} ${student.surname ?? ''}`.trim(),
      student.numberValue || student.number || '-',
      typeof student.totalScore === 'number' ? student.totalScore : getStudentTotal(student),
    ])
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...body])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Öğrenciler')

    const wbout = XLSX.write(workbook, {bookType: 'xlsx', type: 'binary'})
    const buffer = new ArrayBuffer(wbout.length)
    const view = new Uint8Array(buffer)
    for (let i = 0; i < wbout.length; i++) {
      view[i] = wbout.charCodeAt(i) & 0xff
    }
    const blob = new Blob([buffer], {type: 'application/octet-stream'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const examLabel = (exam?.name || `sinav-${normalizedExamId || ''}`).toString().trim().replace(/\s+/g, '-')
    link.href = url
    link.setAttribute('download', `ogrenci-notlari-${examLabel || 'liste'}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [
    exam?.name,
    getExamResultStudents,
    getStudentTotal,
    normalizedExamId,
    sortDirection,
    sortField,
    studentTableRows,
    totalStudentCount,
    useServerPaging,
  ])

  const handleSave = useCallback(async () => {
    if (!Array.isArray(students) || students.length === 0) {
      return
    }

    if (!isPageActive) {
      Swal.fire({
        title: 'Uyarı',
        text: disableReason || 'Bu sınav için puan girişi şu anda aktif değil.',
        icon: 'warning',
        confirmButtonText: 'Tamam',
      })
      return
    }

    try {
      setSaving(true)
      const payload = students.map((student) => {
        const studentId = student.studentSemesterCourseId
        const manualScores = scores[studentId] || {}

        const existingResults = studentResultLookup[studentId] || {}
        const resultEntries = Object.entries(existingResults).reduce((list, [questionId, result]) => {
          const normalizedId = Number(questionId)
          if (!Number.isFinite(normalizedId)) {
            return list
          }

          const normalizedPoint = normalizeScoreValue(result?.point) ?? 0
          list.push({examQuestionId: normalizedId, point: normalizedPoint})
          return list
        }, [])

        openEndedQuestions.forEach((question) => {
          const manual = manualScores[question.id]
          const point = typeof manual === 'number' ? manual : 0
          const existingIndex = resultEntries.findIndex((entry) => entry.examQuestionId === question.id)
          if (existingIndex !== -1) {
            resultEntries[existingIndex] = {
              ...resultEntries[existingIndex],
              point,
            }
          } else {
            resultEntries.push({examQuestionId: question.id, point})
          }
        })

        const results = resultEntries.length
          ? resultEntries
          : questions.map((question) => ({
              examQuestionId: question.id,
              point: question.isMultipleChoice
                ? 0
                : typeof manualScores[question.id] === 'number'
                ? manualScores[question.id]
                : 0,
            }))

        return {
          studentSemesterCourseId: studentId,
          examResults: results,
        }
      })

      await axios.post(SAVE_RESULTS, payload)
      Swal.fire({title: 'Başarılı!', text: 'Puanlar kaydedildi.', icon: 'success', confirmButtonText: 'Tamam'})

      await loadData({
        silentOverview: true,
        silentQuestions: true,
        studentOptions: buildStudentRequestOptions({silent: true}),
      })
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        'Puanlar kaydedilirken bir hata oluştu.'
      Swal.fire({title: 'Hata', text: message, icon: 'error', confirmButtonText: 'Tamam'})
    } finally {
      setSaving(false)
    }
  }, [
    buildStudentRequestOptions,
    disableReason,
    isPageActive,
    loadData,
    openEndedQuestions,
    questions,
    scores,
    students,
    studentResultLookup,
  ])

  const handleApplyFilters = useCallback(() => {
    setAppliedNameFilter(nameFilterInput)
    setAppliedNumberFilter(numberFilterInput)
    setPageNumber(1)
  }, [nameFilterInput, numberFilterInput])

  const handleResetFilters = useCallback(() => {
    setNameFilterInput('')
    setNumberFilterInput('')
    setAppliedNameFilter('')
    setAppliedNumberFilter('')
    setPageNumber(1)
  }, [])

  const handleSortFieldChange = useCallback((event) => {
    setSortField(event?.target?.value || 'name')
    setPageNumber(1)
  }, [])

  const handleSortDirectionToggle = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    setPageNumber(1)
  }, [])

  const handlePageSizeChange = useCallback((event) => {
    const value = Number(event?.target?.value)
    const normalized = Number.isFinite(value) && value > 0 ? value : 50
    setPageSize(normalized)
    setPageNumber(1)
  }, [])

  const handlePageChange = useCallback(
    (nextPage) => {
      setPageNumber((prev) => {
        const maxPage = Math.max(totalPages, 1)
        if (!Number.isFinite(nextPage)) {
          return prev
        }

        const target = Math.min(Math.max(nextPage, 1), maxPage)
        return target
      })
    },
    [totalPages]
  )

  const selectedStudent = useMemo(
    () => {
      const baseStudent = students.find((student) => student.studentSemesterCourseId === previewStudentId)

      if (!baseStudent) {
        return null
      }

      const cachedDetails = studentDetailsCache[baseStudent.studentSemesterCourseId]
      if (cachedDetails && typeof cachedDetails === 'object') {
        return {...baseStudent, ...cachedDetails}
      }

      return baseStudent
    },
    [studentDetailsCache, students, previewStudentId]
  )

  const selectedStudentAnswers = useMemo(
    () => normalizeStudentAnswers(selectedStudent),
    [selectedStudent]
  )

  useEffect(() => {
    if (!previewStudentId || !Number.isFinite(normalizedExamId)) {
      return
    }

    const cacheAvailable = Object.prototype.hasOwnProperty.call(studentDetailsCache, previewStudentId)
    if (cacheAvailable) {
      return
    }

    const targetStudent = students.find((student) => student.studentSemesterCourseId === previewStudentId)
    const hasInlineDetails =
      (Array.isArray(targetStudent?.examResults) && targetStudent.examResults.length > 0) ||
      normalizeStudentAnswers(targetStudent).length > 0

    if (hasInlineDetails) {
      return
    }

    let isActive = true

    const fetchDetails = async () => {
      try {
        const details = await getExamResultStudentDetails(normalizedExamId, previewStudentId)
        if (!isActive) {
          return
        }

        setStudentDetailsCache((prev) => ({...prev, [previewStudentId]: details || {}}))
      } catch (error) {
        if (!isActive) {
          return
        }

        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data ||
          error?.message ||
          'Öğrenci cevapları yüklenirken bir hata oluştu.'

        Swal.fire({title: 'Hata', text: message, icon: 'error', confirmButtonText: 'Tamam'})
      }
    }

    fetchDetails()

    return () => {
      isActive = false
    }
  }, [normalizedExamId, previewStudentId, studentDetailsCache, students])

  const selectedStudentBookletCode = useMemo(() => {
    if (!selectedStudent) {
      return ''
    }

    return resolveStudentBookletCode(selectedStudent)
  }, [selectedStudent])

  const selectedStudentBookletLabel = useMemo(() => {
    if (!selectedStudent) {
      return null
    }

    return resolveBookletLabel(selectedStudentBookletCode || selectedStudent.bookletCode)
  }, [selectedStudent, selectedStudentBookletCode])

  const studentFilterControls = (
    <div className='border rounded p-3 mb-4 bg-light'>
      <div className='row g-3'>
        <div className='col-12 col-lg-4'>
          <label className='form-label mb-1'>Öğrenci Adı Soyadı</label>
          <input
            type='text'
            className='form-control'
            placeholder='Ad veya soyad'
            value={nameFilterInput}
            onChange={(event) => setNameFilterInput(event.target.value)}
          />
        </div>
        <div className='col-12 col-lg-4'>
          <label className='form-label mb-1'>Öğrenci Numarası</label>
          <input
            type='text'
            className='form-control'
            placeholder='Öğrenci no'
            value={numberFilterInput}
            onChange={(event) => setNumberFilterInput(event.target.value)}
          />
        </div>
        <div className='col-12 col-lg-4'>
          <label className='form-label mb-1'>Sıralama</label>
          <div className='d-flex gap-2'>
                      <select className='form-select' style={{ width: "auto" }} value={sortField} onChange={handleSortFieldChange}>
              <option value='name'>Ad Soyad</option>
              <option value='number'>Öğrenci No</option>
              <option value='total'>Toplam Puan</option>
            </select>
            <button
              type='button'
              className='btn btn-outline-primary'
              onClick={handleSortDirectionToggle}
            >
              <i className={`bi ${sortDirection === 'asc' ? 'bi-sort-alpha-down' : 'bi-sort-alpha-up'}`}></i>
              <span className='ms-2'>{sortDirection === 'asc' ? 'Artan' : 'Azalan'}</span>
            </button>
          </div>
        </div>
      </div>
      <div className='d-flex flex-wrap align-items-center gap-2 mt-3'>
        <div className='d-flex gap-2'>
          <button type='button' className='btn btn-primary' onClick={handleApplyFilters}>
            Filtrele / Ara
          </button>
          <button type='button' className='btn btn-light' onClick={handleResetFilters}>
            Temizle
          </button>
          <button
            type='button'
            className='btn btn-outline-success'
            onClick={downloadStudentExcel}
            disabled={!sortedStudents.length}
          >
            Excel İndir
          </button>
        </div>
        <div className='text-muted small ms-auto'>
          {useServerPaging || filteredStudents.length === students.length
            ? `Toplam ${totalStudentCount} öğrenci`
            : `Filtrelenen ${filteredStudents.length}/${totalStudentCount} öğrenci`}
        </div>
      </div>
    </div>
  )

  const studentPaginationControls = (
    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'>
      <div className='text-muted small'>
        {totalStudentCount === 0
          ? 'Gösterilecek öğrenci yok.'
          : `${(pageNumber - 1) * effectivePageSize + 1}-${
              (pageNumber - 1) * effectivePageSize + paginatedStudents.length
            } arası gösteriliyor · Sayfa ${pageNumber}/${totalPages}`}
      </div>
      <div className='d-flex flex-wrap align-items-center gap-2'>
        <label className='form-label mb-0'>Sayfa boyutu</label>
        <select
          className='form-select form-select-sm w-auto'
          value={pageSize}
          onChange={(event) => handlePageSizeChange(event)}
        >
          {[10, 25, 50, 100].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <div className='btn-group'>
          <button
            type='button'
                      className='btn btn-outline-warning btn-sm'
            onClick={() => handlePageChange(pageNumber - 1)}
            disabled={pageNumber <= 1}
          >
            Önceki
          </button>
          <button
            type='button'
            className='btn btn-outline-warning btn-sm'
            onClick={() => handlePageChange(pageNumber + 1)}
            disabled={pageNumber >= totalPages || totalStudentCount === 0}
          >
            Sonraki
          </button>
        </div>
      </div>
    </div>
  )

  const previewQuestions = useMemo(() => {
    if (!selectedStudent) {
      return questions
    }

    const normalizedCode = selectedStudentBookletCode

    const resolveOrder = (question) => {
      const entries = questionBookletOrders[question.id] || []

      if (entries.length > 0) {
        const targetEntry = entries.find(
          (entry) => entry && entry.code === normalizedCode && Number.isFinite(entry.questionNumber)
        )

        if (targetEntry && Number.isFinite(targetEntry.questionNumber)) {
          return Number(targetEntry.questionNumber)
        }

        const masterEntry = entries.find(
          (entry) => entry && (!entry.code || entry.code === '') && Number.isFinite(entry.questionNumber)
        )

        if (masterEntry && Number.isFinite(masterEntry.questionNumber)) {
          return Number(masterEntry.questionNumber)
        }
      }

      if (Number.isFinite(question.order)) {
        return Number(question.order)
      }

      return Number.MAX_SAFE_INTEGER
    }

    return [...questions].sort((a, b) => {
      const orderA = resolveOrder(a)
      const orderB = resolveOrder(b)

      if (orderA !== orderB) {
        return orderA - orderB
      }

      const baseA = Number.isFinite(a.order) ? Number(a.order) : Number.MAX_SAFE_INTEGER
      const baseB = Number.isFinite(b.order) ? Number(b.order) : Number.MAX_SAFE_INTEGER

      if (baseA !== baseB) {
        return baseA - baseB
      }

      const idA = Number.isFinite(a.id) ? Number(a.id) : Number.MAX_SAFE_INTEGER
      const idB = Number.isFinite(b.id) ? Number(b.id) : Number.MAX_SAFE_INTEGER

      return idA - idB
    })
  }, [questions, questionBookletOrders, selectedStudent, selectedStudentBookletCode])

  const closePreview = useCallback(() => {
    setPreviewStudentId(null)
  }, [])

  const isRefreshing = loading && hasInitialData

  if (!Number.isFinite(normalizedExamId)) {
    return <div className='alert alert-danger'>Sınav bilgisi bulunamadı.</div>
  }

  if (loading && !hasInitialData) {
    return (
      <div className='d-flex justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Yükleniyor...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='container-fluid py-5'>
      {error && <div className='alert alert-danger mb-5'>{error}</div>}
      {opticalObjectionNote && (
        <div className='alert alert-danger mb-5' role='alert'>
          <div className='fw-semibold mb-1'>İtiraz Notu</div>
          <div>{opticalObjectionNote}</div>
        </div>
      )}

      {isRefreshing && (
        <div className='alert alert-info d-flex align-items-center gap-3' role='status'>
          <span className='spinner-border spinner-border-sm text-info' aria-hidden='true'></span>
          <div className='d-flex flex-column'>
            <span className='fw-semibold'>Veriler güncelleniyor</span>
            <span className='text-muted'>Özet ve soru bilgileri yenileniyor, mevcut içerik görüntülenmeye devam ediyor.</span>
          </div>
        </div>
      )}

      {exam && (
        <div className='mb-6'>
          <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between gap-4'>
            <div>
              <h2 className='fw-semibold mb-1'>
                {exam.courseCode ? `${exam.courseCode} - ` : ''}
                {exam.name || 'Sınav Sonuç Girişi'}
              </h2>
              <div className='text-muted'>
                {exam.semesterName ? `${exam.semesterName} · ` : ''}
                {exam.examType || 'Sınav'}
              </div>
            </div>
            {obsTransfer && (
              <Tippy
                content={
                  !canSendObs && obsDisabledReason
                    ? obsDisabledReason
                    : "OBS'ye gönderim yapılabilir."
                }
                disabled={canSendObs || !obsDisabledReason}
              >
                <span className='d-inline-flex'>
                  <button
                    type='button'
                    className={`btn ${canSendObs ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                    disabled={!canSendObs || obsSending}
                    onClick={canSendObs ? handleSendGradesToObs : undefined}
                  >
                    {obsSending ? (
                      <>
                        <span
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                          aria-hidden='true'
                        ></span>
                        OBS gönderiliyor...
                      </>
                    ) : (
                      "OBS'ye Gönder"
                    )}
                  </button>
                </span>
              </Tippy>
            )}
          </div>
        </div>
      )}

      {linkedExams.length > 0 && (
        <div className='card mb-6'>
          <div className='card-header'>
            <div className='card-title d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-3 w-100'>
              <div>
                <h4 className='mb-1'>Sınav Grubu</h4>
                <div className='text-muted'>Toplam {totalStudentCount} öğrenci</div>
              </div>
              <div className='d-flex flex-wrap gap-2'>
                <span className='badge badge-light-primary'>Ana sınav ID: {exam?.baseExamId ?? exam?.id}</span>
                {hasLinkedGroup && <span className='badge badge-light-info'>Gruplanmış {linkedExams.length} sınav</span>}
              </div>
            </div>
          </div>
          <div className='card-body p-0'>
            <div className='table-responsive'>
              <table className='table table-sm table-striped mb-0'>
                <thead>
                  <tr>
                    <th>Sınav</th>
                    <th>Ders</th>
                    <th>Öğrenci Sayısı</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedExams.map((linked) => {
                    const isBase = exam?.baseExamId && linked.id === exam.baseExamId
                    return (
                      <tr key={linked.id}>
                        <td>
                          <div className='d-flex align-items-center gap-2'>
                            <span>{linked.name || '-'}</span>
                            {isBase && <span className='badge badge-light-success'>Ana sınav</span>}
                            {linked.id === exam?.id && !isBase && <span className='badge badge-light'>Bu kayıt</span>}
                          </div>
                        </td>
                        <td>
                          {linked.courseCode ? `${linked.courseCode} · ` : ''}
                          {linked.courseName || '-'}
                        </td>
                        <td>{linked.studentCount ?? 0}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {disableReason && (
        <div className='alert alert-warning d-flex align-items-center gap-3'>
          <i className='bi bi-exclamation-triangle-fill fs-2 text-warning'></i>
          <div>
            <div className='fw-semibold mb-1'>Puan girişi şu anda pasif.</div>
            <div className='mb-0'>{disableReason}</div>
          </div>
        </div>
      )}

      {summary && multipleChoiceQuestions.length > 0 && (
        <div className='card mb-6'>
          <div className='card-header'>
            <div className='card-title w-100'>
              <div className='d-flex flex-column flex-xl-row gap-3 align-items-xl-center justify-content-xl-between w-100'>
                <div>
                  <h4 className='mb-1'>Çoktan Seçmeli Soru Özeti</h4>
                  <div className='text-muted'>Optik okuma verileri</div>
                </div>
                <div className='d-flex flex-column flex-xl-row gap-3 align-items-xl-center justify-content-xl-end w-100'>
                  <div className='d-flex flex-wrap gap-2 justify-content-start justify-content-xl-end'>
                    {STATUS_SEQUENCE.map((status) => (
                      <span key={status} className={`badge ${STATUS_BADGE_CLASSES[status]} fw-semibold`}>
                        {STATUS_LABELS[status]}: {statusCounts ? statusCounts[status] ?? 0 : 0}
                      </span>
                    ))}
                    <span className='badge badge-light'>Eksik öğrenci: {missingStudentCount}</span>
                  </div>
                  {canEditAnswerKey && (answerKeySyncing || hasQueuedAnswerKeyUpdates) && (
                    <div className='d-flex align-items-center gap-2 text-muted small'>
                      {answerKeySyncing ? (
                        <>
                          <span
                            className='spinner-border spinner-border-sm text-primary'
                            role='status'
                            aria-hidden='true'
                          ></span>
                          <span>Cevap anahtarı güncelleniyor...</span>
                        </>
                      ) : (
                        <span>Bekleyen cevap anahtarı güncellemeleri uygulanacak...</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='card-body'>
            <div className='table-responsive mb-5'>
              <table className='table table-sm table-bordered mb-0'>
                <tbody>
                  <tr>
                    <th scope='row'>Toplam Kayıt</th>
                    <td>{summary.totalEntries ?? 0}</td>
                    <th scope='row'>Eşleşen</th>
                    <td>{summary.matchedEntries ?? 0}</td>
                    <th scope='row'>Eksik Öğrenci</th>
                    <td>{missingStudentCount}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Mükerrer</th>
                    <td>{summary.duplicateEntries ?? 0}</td>
                    <th scope='row'>Kayıtsız</th>
                    <td>{summary.unregisteredEntries ?? 0}</td>
                    <th scope='row'>Kitapçık Hatası</th>
                    <td>{summary.bookletMismatchEntries ?? 0}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Birden Fazla Ders</th>
                    <td>{summary.ambiguousEntries ?? 0}</td>
                    <th scope='row'>Yoksayılan</th>
                    <td>{summary.skippedEntries ?? 0}</td>
                    <th scope='row'>Geçersiz</th>
                    <td>{summary.invalidEntries ?? 0}</td>
                  </tr>
                  <tr>
                    <th scope='row'>Ortalama</th>
                    <td>{formatNumber(averageScore)}</td>
                    <th scope='row'>Medyan</th>
                    <td>{formatNumber(medianScore)}</td>
                    <th scope='row'>Std. Sapma</th>
                    <td>{formatNumber(summary.standardDeviation)}</td>
                  </tr>
                  <tr>
                    <th scope='row'>En Düşük</th>
                    <td>{minimumScore !== null ? formatNumber(minimumScore) : '-'}</td>
                    <th scope='row'>En Yüksek</th>
                    <td>{maximumScore !== null ? formatNumber(maximumScore) : '-'}</td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr>
                    <th scope='row'>
                      <div className='d-inline-flex align-items-center gap-2'>
                        <span>α (0/1)</span>
                        <Tippy content={CRONBACH_ALPHA_TOOLTIP} maxWidth={320} placement='top'>
                          <span className='text-primary d-inline-flex align-items-center justify-content-center' style={{cursor: 'pointer'}}>
                            <i className='bi bi-question-circle-fill fs-5'></i>
                          </span>
                        </Tippy>
                      </div>
                    </th>
                    <td colSpan={5}>{formatNumber(summary.cronbachAlpha01 ?? summary.cronbachAlpha)}</td>
                  </tr>
                  <tr>
                    <th scope='row'>
                      <div className='d-inline-flex align-items-center gap-2'>
                        <span>KR-20</span>
                        <Tippy content={KR20_TOOLTIP} maxWidth={320} placement='top'>
                          <span className='text-primary d-inline-flex align-items-center justify-content-center' style={{cursor: 'pointer'}}>
                            <i className='bi bi-question-circle-fill fs-5'></i>
                          </span>
                        </Tippy>
                      </div>
                    </th>
                    <td colSpan={5}>{formatNumber(summary.kr20)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {getCronbachAlphaComment(
              summary.cronbachAlpha01 ?? summary.cronbachAlpha,
              multipleChoiceQuestionAnalyses.length
            ) && (
              <div className='alert alert-light-primary py-2 px-3 mb-5'>
                <div className='fw-semibold mb-1'>Cronbach Alpha Yorumu</div>
                <div className='mb-0'>
                  {getCronbachAlphaComment(
                    summary.cronbachAlpha01 ?? summary.cronbachAlpha,
                    multipleChoiceQuestionAnalyses.length
                  )}
                </div>
              </div>
            )}

            <div className='border rounded-3 p-4 mb-5'>
              <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between gap-3 mb-3'>
                <div>
                  <h5 className='mb-1'>4 Yanlış 1 Doğruyu Götürür</h5>
                  <div className='text-muted'>Ceza uygulanacaksa değerleri buradan güncelleyebilirsiniz.</div>
                </div>
                <div className='form-check form-switch m-0'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='penalty-toggle'
                    checked={penaltyState.applyPenalty}
                    onChange={handlePenaltyToggle}
                    disabled={!canEditAnswerKey}
                  />
                  <label className='form-check-label ms-2' htmlFor='penalty-toggle'>Ceza uygula</label>
                </div>
              </div>
              <div className='row g-3'>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label form-label-sm'>Kaç yanlışta ceza?</label>
                  <input
                    type='number'
                    className='form-control form-control-sm'
                    min='1'
                    value={penaltyState.wrongCount}
                    onChange={(event) => handlePenaltyValueChange('wrongCount', event.target.value)}
                    disabled={!penaltyState.applyPenalty || !canEditAnswerKey}
                  />
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label form-label-sm'>Kaç doğru düşsün?</label>
                  <input
                    type='number'
                    className='form-control form-control-sm'
                    min='1'
                    value={penaltyState.correctDeduction}
                    onChange={(event) => handlePenaltyValueChange('correctDeduction', event.target.value)}
                    disabled={!penaltyState.applyPenalty || !canEditAnswerKey}
                  />
                </div>
                <div className='col-12 col-lg-6 align-self-end'>
                  <div className='text-muted small'>Örn: 4 yanlış 1 doğruyu götürür için değerler 4 ve 1 olmalıdır.</div>
                </div>
              </div>
            </div>

            {multipleChoiceQuestionAnalyses.length > 0 && (
              <div className='table-responsive'>
                <table className='table table-sm table-striped table-bordered mb-0 align-middle'>
                  <thead>
                  <tr>
                    <th>Soru</th>
                    <th>Kitapçık Sırası</th>
                    <th>Yanıtlayan</th>
                    <th>Doğru</th>
                    <th>Boş</th>
                    <th>Master Kitapçık Yanıtı</th>
                    <th>İptal</th>
                    <th>Güçlük</th>
                    <th>Ayrırtedicilik</th>
                    <th>YZ Analizi</th>
                  </tr>
                </thead>
                  <tbody>
                    {multipleChoiceQuestionAnalyses.map((analysis) => {
                      const mappingEntries = questionBookletOrders[analysis.examQuestionId] || []
                      const mappingLabel =
                        mappingEntries.length > 0
                          ? mappingEntries.map((entry) => `${entry.label}: ${entry.questionNumber}`).join(', ')
                          : '-'
                      const difficultyInsight = resolveDifficultyInsight(analysis)
                      const discriminationInsight = resolveDiscriminationInsight(analysis)
                      const answerEntry = answerKeyState[analysis.examQuestionId] || null
                      const masterMapEntry = bookletQuestionMap['']?.questions?.[analysis.examQuestionId] || null
                      const masterAccepted = Array.isArray(answerEntry?.master) && answerEntry.master.length > 0
                        ? answerEntry.master
                        : Array.isArray(masterMapEntry?.acceptedOptions)
                        ? masterMapEntry.acceptedOptions
                        : []
                      const inputValue = Object.prototype.hasOwnProperty.call(
                        answerKeyInputs,
                        analysis.examQuestionId
                      )
                        ? answerKeyInputs[analysis.examQuestionId]
                        : masterAccepted.join(', ')
                      const isCancelled = Boolean(questionCancellationState[analysis.examQuestionId])
                      return (
                        <tr key={analysis.examQuestionId}>
                          <td>
                            <button
                              type='button'
                              className='btn btn-link p-0 text-decoration-none fw-semibold'
                              onClick={() =>
                                openQuestionPreview(analysis.examQuestionId, analysis.questionNumber ?? null)
                              }
                              title='Soru önizlemesini aç'
                            >
                              {analysis.questionNumber ?? '-'}
                            </button>
                          </td>
                          <td>{mappingLabel}</td>
                          <td>{analysis.respondentCount ?? 0}</td>
                          <td>{analysis.correctCount ?? 0}</td>
                          <td>{analysis.blankCount ?? 0}</td>
                          <td style={{minWidth: '220px'}}>
                            <div className='d-flex flex-column gap-2'>
                              <div className='text-muted small'>Kaydedilen: {masterAccepted.length > 0 ? masterAccepted.join(', ') : '-'}</div>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                value={inputValue}
                                onChange={(event) =>
                                  handleMasterAnswerInputChange(analysis.examQuestionId, event.target.value)
                                }
                                placeholder='Örn: A, B'
                                disabled={!canEditAnswerKey}
                              />
                            </div>
                          </td>
                          <td className='text-center'>
                            <div className='form-check form-check-sm form-check-custom form-check-solid d-flex justify-content-center'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={isCancelled}
                                onChange={(event) =>
                                  handleToggleQuestionCancellation(analysis.examQuestionId, event.target.checked)
                                }
                                disabled={!canEditAnswerKey}
                              />
                            </div>
                          </td>
                          <td className={getInsightClassName(difficultyInsight)}>
                            {formatNumber(analysis.difficulty)} · {difficultyInsight.label}
                          </td>
                          <td className={getInsightClassName(discriminationInsight)}>
                            {formatNumber(analysis.discrimination)} · {discriminationInsight.label}
                          </td>
                          <td>
                            {(() => {
                              const cacheEntry = questionAiReviewCache[analysis.examQuestionId] || null
                              const isPending = Boolean(autoReviewPendingMap[analysis.examQuestionId])
                              const aiCommentFromAnalysis =
                                typeof analysis.aiReviewComment === 'string' && analysis.aiReviewComment.trim().length > 0
                                  ? analysis.aiReviewComment.trim()
                                  : null
                              const aiUpdatedRaw = cacheEntry?.updatedAt || analysis.aiReviewUpdatedAt || null
                              const aiUpdated = aiUpdatedRaw
                                ? new Date(aiUpdatedRaw).toLocaleString('tr-TR')
                                : null
                              const errorMessage = cacheEntry?.error

                              const formattedCommentHtml = (() => {
                                if (cacheEntry?.comment) {
                                  return cacheEntry.formattedHtml || formatAiCommentHtml(cacheEntry.comment)
                                }
                                if (aiCommentFromAnalysis) {
                                  return formatAiCommentHtml(aiCommentFromAnalysis)
                                }
                                return null
                              })()

                              if (formattedCommentHtml) {
                                return (
                                  <div className='d-flex flex-column gap-2 align-items-start w-100'>
                                    <div
                                      className='alert alert-light-info border border-info-subtle mb-0 py-2 px-3 w-100'
                                      style={AI_COMMENT_PREVIEW_STYLE}
                                      dangerouslySetInnerHTML={{__html: formattedCommentHtml}}
                                    ></div>
                                    {aiUpdated && (
                                      <div className='text-muted fs-8'>Son güncelleme: {aiUpdated}</div>
                                    )}
                                  </div>
                                )
                              }

                              if (isPending) {
                                return (
                                  <span className='badge badge-light-info'>Yapay zekâ yanıtı bekleniyor...</span>
                                )
                              }

                              if (errorMessage) {
                                return (
                                  <div className='alert alert-light-danger border border-danger-subtle mb-0 py-2 px-3'>
                                    {errorMessage}
                                  </div>
                                )
                              }

                              if (analysis.requiresAiReview) {
                                return <span className='badge badge-light-warning'>İnceleme öneriliyor</span>
                              }

                              return <span className='text-muted'>Analiz bulunmuyor</span>
                            })()}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className='card mb-6'>
        <div className='card-header'>
          <div className='card-title d-flex flex-column flex-lg-row gap-3 justify-content-lg-between w-100'>
            <div>
              <h4 className='mb-1'>Öğrenci Puan Girişi</h4>
              <div className='text-muted'>Açık uçlu sorular için puanlama</div>
            </div>
            <div className='d-flex gap-2'>
              {openEndedQuestions.length > 0 && (
                <button
                  type='button'
                  className='btn btn-success'
                  onClick={handleSave}
                  disabled={!isPageActive || saving}
                >
                  {saving ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                      Nihai sınav sonuçları kaydediliyor...
                    </>
                  ) : (
                    'Nihai Sınav Sonuçlarını Kaydet'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
            <div className='card-body'>
          {openEndedQuestions.length === 0 ? (
            <div className='alert alert-info mb-0'>Bu sınavda puanlanacak açık uçlu soru bulunmuyor.</div>
          ) : (
            <>
              <div className='border rounded p-3 mb-4 bg-light'>
                {studentFilterControls}
              </div>

              {studentPaginationControls}

              {studentError && (
                <div className='alert alert-danger d-flex align-items-center gap-2' role='alert'>
                  <i className='bi bi-exclamation-triangle-fill fs-3 text-danger'></i>
                  <span>{studentError}</span>
                </div>
              )}

              {studentLoading && (
                <div className='text-center my-4' data-testid='student-list-loading'>
                  <div className='spinner-border text-primary' role='status' aria-label='Öğrenciler yükleniyor'></div>
                </div>
              )}

              <div className='table-responsive'>
                <table className='table table-striped table-bordered align-middle'>
                  <thead>
                  <tr>
                    <th>Öğrenci</th>
                    <th>Numara</th>
                    <th>Fakülte</th>
                    <th>Bölüm</th>
                    {openEndedQuestions.map((question) => (
                        <th key={question.id} className='text-center'>
                          <button
                            type='button'
                            className='btn btn-link p-0 text-decoration-none text-gray-900 fw-semibold d-inline-flex flex-column align-items-center w-100'
                            onClick={() => openQuestionPreview(question.id, question.order ?? null)}
                            title='Soru önizlemesini aç'
                          >
                            <span className='mb-1'>Soru {question.order ?? '-'}</span>
                            <span className='text-muted fs-8 fw-normal'>Maks. {formatNumber(question.point)}</span>
                          </button>
                        </th>
                      ))}
                      <th>Toplam Puan</th>
                      <th>Önizleme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStudents.map((student) => (
                      <tr key={student.studentSemesterCourseId}>
                        <td>{student.fullName}</td>
                        <td>{student.numberValue || '-'}</td>
                        <td>{student.facultyDisplay ?? '-'}</td>
                        <td>{student.departmentDisplay ?? '-'}</td>
                        {openEndedQuestions.map((question) => (
                          <td key={question.id}>
                            <input
                              type='number'
                              className='form-control form-control-sm'
                              value={getStudentScore(student.studentSemesterCourseId, question.id)}
                              onChange={(event) =>
                                handleScoreChange(student.studentSemesterCourseId, question.id, event.target.value)
                              }
                              disabled={!isPageActive || saving}
                              step='0.01'
                              min='0'
                              max={typeof question.point === 'number' ? question.point : undefined}
                            />
                          </td>
                        ))}
                        <td>{formatNumber(student.totalScore)}</td>
                        <td>
                          <button
                            type='button'
                            className='btn btn-light btn-sm'
                            onClick={() => setPreviewStudentId(student.studentSemesterCourseId)}
                          >
                            Önizle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {questionPreviewContext && (() => {
        const {examQuestionId, questionNumber} = questionPreviewContext
        const question = questions.find((item) => item.id === examQuestionId) || null
        const baseNumber =
          typeof questionNumber === 'number'
            ? questionNumber
            : typeof question?.order === 'number'
              ? question.order
              : null
        const modalTitle = baseNumber !== null ? `Soru ${baseNumber} Detayı` : 'Soru Detayı'
        const questionHtml = enhanceQuestionHtml(question?.text || question?.title || '')
        const sharedContentHtml = enhanceQuestionHtml(question?.sharedContentContent || '')
        const sharedContentTitle = question?.sharedContentTitle

        const optionItems = Array.isArray(question?.questionItems) ? question.questionItems : []
        const optionDescriptions = optionItems.map((item, index) => {
          const resolvedLabel = resolveQuestionItemLabel(item, index)
          const letter = (resolvedLabel || String.fromCharCode(65 + index)).toUpperCase()
          const html = enhanceQuestionHtml(item?.name || '')

          return {
            key: typeof item?.id === 'number' ? item.id : `option-${index}`,
            letter,
            html,
            isCorrect: Boolean(item?.isTrue),
          }
        })

        const mappingEntries = questionBookletOrders[examQuestionId] || []
        const mappingByCode = mappingEntries.reduce((acc, entry) => {
          const normalizedCode =
            typeof entry?.code === 'string'
              ? entry.code
              : normalizeBookletCode(typeof entry?.label === 'string' ? entry.label : '') || ''
          acc[normalizedCode || ''] = typeof entry?.questionNumber === 'number' ? entry.questionNumber : null
          return acc
        }, {})

        const answerEntry = answerKeyState[examQuestionId] || null
        const masterEntry = bookletQuestionMap['']?.questions?.[examQuestionId]
        const masterAccepted = Array.isArray(answerEntry?.booklets?.['']) && answerEntry.booklets[''].length > 0
          ? answerEntry.booklets['']
          : Array.isArray(masterEntry?.acceptedOptions)
          ? masterEntry.acceptedOptions
          : []
        const masterQuestionNumber =
          typeof masterEntry?.questionNumber === 'number'
            ? masterEntry.questionNumber
            : mappingByCode[''] ?? baseNumber

        const codeSet = new Set(Object.keys(bookletQuestionMap))
        Object.keys(mappingByCode).forEach((code) => codeSet.add(code))
        if (Array.isArray(answerEntry?.codes)) {
          answerEntry.codes.forEach((code) => {
            if (typeof code === 'string') {
              codeSet.add(code)
            }
          })
        }
        if (!codeSet.has('')) {
          codeSet.add('')
        }

        const bookletRows = Array.from(codeSet)
          .map((code) => {
            const info = bookletQuestionMap[code] || null
            const questionInfo = info?.questions?.[examQuestionId]
            const overrideAccepted =
              Array.isArray(answerEntry?.booklets?.[code]) && answerEntry.booklets[code].length > 0
                ? answerEntry.booklets[code]
                : null
            const accepted = overrideAccepted && overrideAccepted.length > 0
              ? overrideAccepted
              : Array.isArray(questionInfo?.acceptedOptions) && questionInfo.acceptedOptions.length > 0
              ? questionInfo.acceptedOptions
              : masterAccepted
            const questionNo = (() => {
              if (typeof questionInfo?.questionNumber === 'number') {
                return questionInfo.questionNumber
              }
              if (Object.prototype.hasOwnProperty.call(mappingByCode, code)) {
                return mappingByCode[code]
              }
              if (code === '') {
                return masterQuestionNumber
              }
              return null
            })()

            const label = (() => {
              if (info?.label) {
                return info.label
              }
              if (code === '') {
                return 'Master Kitapçık'
              }
              return code
            })()

            return {
              code,
              label,
              questionNumber: questionNo,
              accepted: normalizeAcceptedOptionsList(accepted),
            }
          })
          .sort((a, b) => {
            if (a.code === '' && b.code !== '') {
              return -1
            }
            if (a.code !== '' && b.code === '') {
              return 1
            }
            return a.label.localeCompare(b.label, 'tr')
          })

        return (
          <>
            <div className='modal fade show d-block' role='dialog' aria-modal='true'>
              <div className='modal-dialog modal-lg modal-dialog-scrollable'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title'>{modalTitle}</h5>
                    <button type='button' className='btn-close' onClick={closeQuestionPreview} aria-label='Kapat'></button>
                  </div>
                  <div className='modal-body'>
                    {question ? (
                      <>
                        {sharedContentTitle && (
                          <div className='alert alert-light-info py-2 px-3 mb-4'>
                            <div className='fw-semibold mb-1'>{sharedContentTitle}</div>
                            {sharedContentHtml && (
                              <div
                                className='exam-result-html-content'
                                dangerouslySetInnerHTML={{__html: sharedContentHtml}}
                              ></div>
                            )}
                          </div>
                        )}

                        <div className='mb-4'>
                          <div className='fw-semibold mb-2'>Soru</div>
                          <div className='border rounded p-3 bg-light'>
                            <div
                              className='exam-result-html-content'
                              dangerouslySetInnerHTML={{
                                __html:
                                  questionHtml || '<span class="text-muted">Soru metni bulunamadı.</span>',
                              }}
                            ></div>
                          </div>
                        </div>

                        <div className='mb-4'>
                          <div className='fw-semibold mb-2'>Seçenekler</div>
                          {optionDescriptions.length > 0 ? (
                            <ul className='list-unstyled mb-0 d-flex flex-column gap-2'>
                              {optionDescriptions.map((option) => (
                                <li key={option.key} className={option.isCorrect ? 'fw-semibold text-success' : ''}>
                                  <div className='d-flex gap-3 align-items-start'>
                                    <span className='text-uppercase pt-1'>{option.letter}</span>
                                    <div>
                                      <div
                                        className='exam-result-html-content'
                                        dangerouslySetInnerHTML={{__html: option.html || '-'}}
                                      ></div>
                                      {option.isCorrect && (
                                        <span className='badge badge-light-success mt-2'>Doğru</span>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className='text-muted'>Seçenek bilgisi bulunamadı.</div>
                          )}
                        </div>

                        <div className='table-responsive'>
                          <table className='table table-sm table-striped align-middle mb-0'>
                            <thead>
                              <tr>
                                <th>Kitapçık</th>
                                <th>Soru No</th>
                                <th>Doğru Seçenek</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bookletRows.length > 0 ? (
                                bookletRows.map((row) => (
                                  <tr key={`booklet-row-${row.code || 'master'}`}>
                                    <td>{row.label}</td>
                                    <td>{row.questionNumber ?? '-'}</td>
                                    <td>{row.accepted.length > 0 ? row.accepted.join(', ') : '-'}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={3} className='text-center text-muted'>Kitapçık bilgisi bulunamadı.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className='text-muted'>Soru detayları bulunamadı.</div>
                    )}
                  </div>
                  <div className='modal-footer'>
                    <button type='button' className='btn btn-light' onClick={closeQuestionPreview}>
                      Kapat
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-backdrop fade show'></div>
          </>
        )
      })()}

      {students.length > 0 && openEndedQuestions.length === 0 && (
        <div className='card'>
          <div className='card-header'>
            <div className='card-title'>Öğrenci Bazlı Soru Önizlemesi</div>
          </div>
          <div className='card-body'>
            {studentFilterControls}
            {studentPaginationControls}
            <div className='table-responsive'>
              <table className='table table-striped align-middle mb-0'>
                <thead>
                  <tr>
                    <th>Öğrenci</th>
                    <th>Numara</th>
                    <th>Fakülte</th>
                    <th>Bölüm</th>
                    <th>Çoktan Seçmeli Puanı</th>
                    <th>Toplam Puan</th>
                    <th className='text-end pe-5'>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStudents.map((student) => (
                    <tr key={student.studentSemesterCourseId}>
                      <td>{`${student.name ?? ''} ${student.surname ?? ''}`.trim()}</td>
                      <td>{student.number ?? '-'}</td>
                      <td>{resolveStudentFaculty(student)}</td>
                      <td>{resolveStudentDepartment(student)}</td>
                      <td>{formatNumber(getStudentMultipleChoiceScore(student))}</td>
                      <td>{formatNumber(getStudentTotal(student))}</td>
                      <td className='text-end pe-5'>
                        <div className='d-flex justify-content-end gap-2'>
                          {!resolveStudentBookletCode(student) &&
                            getStudentMultipleChoiceScore(student) === 0 &&
                            multipleChoiceQuestions.length > 0 && (
                              <button
                                type='button'
                                className='btn btn-warning btn-sm'
                                onClick={() => openBookletEvaluationModal(student)}
                              >
                                Kitapçık Kodunu Değerlendir
                              </button>
                            )}
                          <button
                            type='button'
                            className='btn btn-light btn-sm'
                            onClick={() => setPreviewStudentId(student.studentSemesterCourseId)}
                          >
                            Önizle
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {bookletEvaluationContext && (
        <>
          <div className='modal fade show d-block' tabIndex={-1} role='dialog'>
            <div className='modal-dialog modal-lg'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>
                    Kitapçık Kodunu Değerlendir · {bookletEvaluationContext.student?.name ?? ''}{' '}
                    {bookletEvaluationContext.student?.surname ?? ''}
                  </h5>
                  <button
                    type='button'
                    className='btn-close'
                    onClick={closeBookletEvaluationModal}
                    aria-label='Kapat'
                  ></button>
                </div>
                <div className='modal-body'>
                  <p className='text-muted'>
                    Öğrencinin kitapçık kodu boş olduğundan çoktan seçmeli puanı hesaplanamadı. Aşağıdaki
                    seçenekler, farklı kitapçık kodları ile değerlendirildiğinde listede görünecek olası puanı gösterir.
                  </p>
                  <div className='table-responsive'>
                    <table className='table table-striped align-middle mb-0'>
                      <thead>
                        <tr>
                          <th>Kitapçık</th>
                          <th>Uygulama Sonrası Puan</th>
                          <th className='text-end pe-5'>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookletEvaluationLoading ? (
                          <tr>
                            <td colSpan={3} className='text-center py-4 text-muted'>
                              Değerlendirme yapılıyor, lütfen bekleyin...
                            </td>
                          </tr>
                        ) : bookletEvaluationContext.evaluations.length > 0 ? (
                          bookletEvaluationContext.evaluations.map((evaluation) => (
                            <tr key={evaluation.code || 'master'}>
                              <td>
                                <div className='d-flex flex-column'>
                                  <span className='fw-semibold'>{evaluation.label}</span>
                                  <span className='text-muted'>{evaluation.code ? `Kod: ${evaluation.code}` : 'Master'}</span>
                                </div>
                              </td>
                              <td>{formatNumber(evaluation.totalScore ?? evaluation.multipleChoiceScore)}</td>
                              <td className='text-end pe-5'>
                                <button
                                  type='button'
                                  className='btn btn-primary btn-sm'
                                  onClick={() => applyBookletEvaluation(evaluation.code)}
                                  disabled={bookletEvaluationApplying !== null}
                                >
                                  {bookletEvaluationApplying === evaluation.code
                                    ? 'Değerlendiriliyor...'
                                    : 'Bu kitapçık ile değerlendir'}
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={3} className='text-center py-4 text-muted'>
                              Hesaplanacak kitapçık sonucu bulunamadı.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-light' onClick={closeBookletEvaluationModal}>
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}

      {selectedStudent && (
        <>
          <div className='modal fade show d-block' tabIndex={-1} role='dialog'>
            <div className='modal-dialog modal-xl modal-dialog-scrollable'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>
                    Soru Önizlemesi · {selectedStudent.name ?? ''} {selectedStudent.surname ?? ''} ({selectedStudent.number ?? '-'})
                  </h5>
                  <button type='button' className='btn-close' onClick={closePreview} aria-label='Kapat'></button>
                </div>
                <div className='modal-body'>
                  {selectedStudentBookletLabel && (
                    <div className='alert alert-light-primary d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-2 mb-5'>
                      <div className='fw-semibold mb-0'>Öğrencinin kitapçığı: {selectedStudentBookletLabel}</div>
                      <div className='text-muted'>
                        {selectedStudentBookletCode ? `Kod: ${selectedStudentBookletCode}` : 'Master kitapçık'}
                      </div>
                    </div>
                  )}

                  {previewQuestions.map((question) => {
                    const examResult = Array.isArray(selectedStudent.examResults)
                      ? selectedStudent.examResults.find((result) => result?.examQuestionId === question.id)
                      : null
                    const studentScore = question.isMultipleChoice
                      ? examResult && typeof examResult?.point === 'number'
                        ? Number(examResult.point)
                        : 0
                      : getStudentScore(selectedStudent.studentSemesterCourseId, question.id)
                    const studentAnswer = selectedStudentAnswers.find(
                      (answer) => answer?.examQuestionId === question.id
                    )
                    const bookletEntries = questionBookletOrders[question.id] || []
                    const selectedOptionTokens = Array.isArray(studentAnswer?.selectedOptionIds)
                      ? studentAnswer.selectedOptionIds
                          .map((value) => normalizeOptionIdentifier(value))
                          .filter((value) => Boolean(value))
                      : []
                    const selectedOptionTokenSet =
                      selectedOptionTokens.length > 0 ? new Set(selectedOptionTokens) : null
                    const selectedPositionOrders = selectedOptionTokens.reduce((set, token) => {
                      const numeric = Number(token)
                      if (Number.isFinite(numeric)) {
                        set.add(numeric)
                      }

                      const letterOrder = mapLetterToOrder(token)
                      if (letterOrder !== null) {
                        set.add(letterOrder)
                      }

                      return set
                    }, new Set())
                    const selectedPositionSet = selectedPositionOrders.size > 0 ? selectedPositionOrders : null

                    const optionOrderMap =
                      question && typeof question.optionOrderByBooklet === 'object' && question.optionOrderByBooklet !== null
                        ? question.optionOrderByBooklet
                        : {}

                    let orderedQuestionItems = Array.isArray(question.questionItems)
                      ? [...question.questionItems]
                      : []

                    let variantOrderApplied = false
                    let optionLetterOverrideMap = null

                    if (orderedQuestionItems.length > 0) {
                      const variantOrder = (() => {
                        if (
                          selectedStudentBookletCode &&
                          Array.isArray(optionOrderMap[selectedStudentBookletCode]) &&
                          optionOrderMap[selectedStudentBookletCode].length > 0
                        ) {
                          return optionOrderMap[selectedStudentBookletCode]
                        }

                        if (Array.isArray(optionOrderMap['']) && optionOrderMap[''].length > 0) {
                          return optionOrderMap['']
                        }

                        if (Array.isArray(optionOrderMap.MASTER) && optionOrderMap.MASTER.length > 0) {
                          return optionOrderMap.MASTER
                        }

                        return null
                      })()

                      if (variantOrder) {
                        variantOrderApplied = Array.isArray(variantOrder) && variantOrder.length > 0
                        const itemsById = new Map()
                        orderedQuestionItems.forEach((item) => {
                          if (item && item.id !== undefined && item.id !== null) {
                            const numericId = Number(item.id)
                            if (Number.isFinite(numericId)) {
                              itemsById.set(numericId, item)
                            }
                          }
                        })

                        const rearranged = []
                        variantOrder.forEach((optionId) => {
                          const numericId = Number(optionId)
                          if (Number.isFinite(numericId) && itemsById.has(numericId)) {
                            const mappedItem = itemsById.get(numericId)
                            if (mappedItem && !rearranged.includes(mappedItem)) {
                              rearranged.push(mappedItem)
                            }
                          }
                        })

                        orderedQuestionItems.forEach((item) => {
                          const numericId = Number(item?.id)
                          if (!Number.isFinite(numericId)) {
                            rearranged.push(item)
                            return
                          }

                          if (!rearranged.some((existing) => Number(existing?.id) === numericId)) {
                            rearranged.push(item)
                          }
                        })

                        orderedQuestionItems = rearranged
                      }
                    }

                    if (variantOrderApplied) {
                      const overrideMap = new Map()
                      orderedQuestionItems.forEach((item, index) => {
                        const numericId = Number(item?.id)
                        if (!Number.isFinite(numericId)) {
                          return
                        }

                        const letter = mapOrderToLetter(index + 1)
                        if (letter) {
                          overrideMap.set(numericId, letter)
                        }
                      })

                      if (overrideMap.size > 0) {
                        optionLetterOverrideMap = overrideMap
                      }
                    }

                    let selectedOptionLabels = []
                    if (orderedQuestionItems.length > 0) {
                      if (selectedOptionTokenSet) {
                        selectedOptionLabels = orderedQuestionItems.reduce((acc, item, index) => {
                          const normalizedItemId = normalizeOptionIdentifier(item?.id)
                          const normalizedOrder = normalizeOptionIdentifier(item?.order)
                          const normalizedKey = normalizeOptionIdentifier(item?.key)
                          const resolvedLabel = resolveQuestionItemLabel(item, index)
                          const normalizedResolvedLabel = normalizeOptionIdentifier(resolvedLabel)

                          const numericItemId = Number(item?.id)
                          const overrideLabel =
                            Number.isFinite(numericItemId) && optionLetterOverrideMap
                              ? optionLetterOverrideMap.get(numericItemId)
                              : null
                          const normalizedOverride = normalizeOptionIdentifier(overrideLabel)

                          const positionMatch = selectedPositionSet ? selectedPositionSet.has(index + 1) : false

                          const identifierTokens = [normalizedItemId, normalizedKey]
                          if (!selectedPositionSet) {
                            identifierTokens.push(normalizedOrder)
                          }

                          const matchesByToken = identifierTokens
                            .filter(Boolean)
                            .some((identifier) => selectedOptionTokenSet.has(identifier))

                          let matchesByLabelToken = false
                          if (selectedOptionTokenSet && selectedOptionTokenSet.size > 0) {
                            if (optionLetterOverrideMap && optionLetterOverrideMap.size > 0 && normalizedOverride) {
                              matchesByLabelToken = selectedOptionTokenSet.has(normalizedOverride)
                            } else if (!variantOrderApplied && normalizedResolvedLabel) {
                              matchesByLabelToken = selectedOptionTokenSet.has(normalizedResolvedLabel)
                            }
                          }

                          const matches = positionMatch || matchesByToken || matchesByLabelToken

                          if (!matches) {
                            return acc
                          }

                          const displayLabel =
                            overrideLabel ||
                            resolvedLabel ||
                            mapOrderToLetter(index + 1) ||
                            String(index + 1)

                          if (displayLabel) {
                            acc.push(displayLabel)
                          }

                          return acc
                        }, [])
                      }

                      if (selectedOptionLabels.length === 0 && question.isMultipleChoice) {
                        selectedOptionLabels = extractOptionLabelsFromAnswerText(studentAnswer?.answerText || '')
                      }
                    } else if (selectedOptionLabels.length === 0 && question.isMultipleChoice) {
                      selectedOptionLabels = extractOptionLabelsFromAnswerText(studentAnswer?.answerText || '')
                    }

                    if (selectedOptionLabels.length > 0) {
                      selectedOptionLabels = normalizeAcceptedOptionsList(selectedOptionLabels)
                    }

                    const selectedOptionLabelSet =
                      selectedOptionLabels.length > 0 ? new Set(selectedOptionLabels) : null

                    const correctOptionLabels = (() => {
                      const normalizedCode = selectedStudentBookletCode || ''
                      const answerEntry = answerKeyState[question.id] || null

                      if (answerEntry && typeof answerEntry === 'object') {
                        const bookletEntries =
                          answerEntry.booklets && typeof answerEntry.booklets === 'object'
                            ? answerEntry.booklets
                            : null

                        if (bookletEntries) {
                          const bookletCodesToTry = normalizedCode ? [normalizedCode, ''] : ['']
                          if (!bookletCodesToTry.includes('MASTER')) {
                            bookletCodesToTry.push('MASTER')
                          }

                          for (const code of bookletCodesToTry) {
                            const options = bookletEntries[code]
                            if (Array.isArray(options) && options.length > 0) {
                              return normalizeAcceptedOptionsList(options)
                            }
                          }
                        }

                        if (Array.isArray(answerEntry.master) && answerEntry.master.length > 0) {
                          return normalizeAcceptedOptionsList(answerEntry.master)
                        }
                      }

                      const bookletInfo = bookletQuestionMap[normalizedCode]?.questions?.[question.id]
                      if (
                        bookletInfo &&
                        Array.isArray(bookletInfo.acceptedOptions) &&
                        bookletInfo.acceptedOptions.length > 0
                      ) {
                        return normalizeAcceptedOptionsList(bookletInfo.acceptedOptions)
                      }

                      const masterInfo = bookletQuestionMap['']?.questions?.[question.id]
                      if (
                        masterInfo &&
                        Array.isArray(masterInfo.acceptedOptions) &&
                        masterInfo.acceptedOptions.length > 0
                      ) {
                        return normalizeAcceptedOptionsList(masterInfo.acceptedOptions)
                      }

                      const fallbackFromQuestion = questionFallbackAcceptedOptions[question.id]
                      if (Array.isArray(fallbackFromQuestion) && fallbackFromQuestion.length > 0) {
                        return normalizeAcceptedOptionsList(fallbackFromQuestion)
                      }

                      if (orderedQuestionItems.length > 0) {
                        return orderedQuestionItems.reduce((acc, item, index) => {
                          if (!item?.isTrue) {
                            return acc
                          }

                          const label = resolveQuestionItemLabel(item, index)
                          if (label && !acc.includes(label)) {
                            acc.push(label)
                          }
                          return acc
                        }, [])
                      }

                      return []
                    })()
                    const correctOptionLabelSet =
                      correctOptionLabels.length > 0 ? new Set(correctOptionLabels) : null

                    const displayQuestionNumber = (() => {
                      if (bookletEntries.length > 0) {
                        const variantEntry = bookletEntries.find(
                          (entry) => entry && entry.code === selectedStudentBookletCode && Number.isFinite(entry.questionNumber)
                        )

                        if (variantEntry && Number.isFinite(variantEntry.questionNumber)) {
                          return variantEntry.questionNumber
                        }

                        const masterEntry = bookletEntries.find(
                          (entry) => entry && (!entry.code || entry.code === '') && Number.isFinite(entry.questionNumber)
                        )

                        if (masterEntry && Number.isFinite(masterEntry.questionNumber)) {
                          return masterEntry.questionNumber
                        }
                      }

                      return question.order ?? ''
                    })()

                    const questionTypeLabel =
                      question.questionTypeName || (question.isMultipleChoice ? 'Çoktan Seçmeli' : 'Açık Uçlu')

                    return (
                      <div key={question.id} className='mb-6 border rounded p-4'>
                        <div className='d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3'>
                          <div>
                            <h5 className='mb-1'>Soru {displayQuestionNumber ?? question.order ?? ''}</h5>
                            <div className='text-muted'>{questionTypeLabel}</div>
                          </div>
                          <div className='d-flex flex-column text-lg-end'>
                            <span className='fw-semibold'>Maksimum Puan: {formatNumber(question.point)}</span>
                            <span className='text-muted'>Girilen Puan: {formatNumber(studentScore)}</span>
                          </div>
                        </div>

                        {question.sharedContentTitle && (
                          <div className='alert alert-light-info py-2 px-3 mb-3'>
                            <div className='fw-semibold mb-1'>{question.sharedContentTitle}</div>
                            {question.sharedContentContent && (
                              <div
                                className='exam-result-html-content mb-0'
                                dangerouslySetInnerHTML={{__html: question.sharedContentContent}}
                              ></div>
                            )}
                          </div>
                        )}

                        <div className='mb-4'>
                          <div className='fw-semibold mb-2'>Soru Metni</div>
                          <div className='border rounded p-3 bg-light'>
                            <div
                              className='exam-result-html-content mb-0'
                              dangerouslySetInnerHTML={{__html: question.text || question.title || ''}}
                            ></div>
                          </div>
                        </div>

                        {orderedQuestionItems.length > 0 && (
                          <div className='mb-4'>
                            <div className='fw-semibold mb-2'>Seçenekler</div>
                            <ul className='mb-0 list-unstyled d-flex flex-column gap-2'>
                              {orderedQuestionItems.map((item, index) => {
                                const numericItemId = Number(item?.id)
                                const resolvedLabel = resolveQuestionItemLabel(item, index)
                                const overrideLabel =
                                  Number.isFinite(numericItemId) && optionLetterOverrideMap
                                    ? optionLetterOverrideMap.get(numericItemId)
                                    : null
                                const displayLabel =
                                  overrideLabel || resolvedLabel || mapOrderToLetter(index + 1) || String(index + 1)
                                let isSelected = false
                                const normalizedOverride = normalizeOptionIdentifier(overrideLabel)
                                const normalizedResolvedToken = normalizeOptionIdentifier(resolvedLabel)
                                if (selectedPositionSet) {
                                  isSelected = selectedPositionSet.has(index + 1)
                                }
                                if (!isSelected && selectedOptionTokenSet) {
                                  const candidateTokens = [
                                    normalizeOptionIdentifier(item?.id),
                                    normalizeOptionIdentifier(item?.key),
                                  ]

                                  if (!selectedPositionSet) {
                                    candidateTokens.push(normalizeOptionIdentifier(item?.order))
                                  }

                                  isSelected = candidateTokens
                                    .filter(Boolean)
                                    .some((token) => selectedOptionTokenSet.has(token))
                                }
                                if (!isSelected && selectedOptionTokenSet && selectedOptionTokenSet.size > 0) {
                                  if (optionLetterOverrideMap && optionLetterOverrideMap.size > 0 && normalizedOverride) {
                                    isSelected = selectedOptionTokenSet.has(normalizedOverride)
                                  } else if (!variantOrderApplied && normalizedResolvedToken) {
                                    isSelected = selectedOptionTokenSet.has(normalizedResolvedToken)
                                  }
                                }
                                if (!isSelected && selectedOptionLabelSet) {
                                  const normalizedDisplayLabel = normalizeOptionValue(displayLabel)
                                  const normalizedResolvedLabel = normalizeOptionValue(resolvedLabel)
                                  if (normalizedDisplayLabel && selectedOptionLabelSet.has(normalizedDisplayLabel)) {
                                    isSelected = true
                                  } else if (
                                    !overrideLabel &&
                                    normalizedResolvedLabel &&
                                    selectedOptionLabelSet.has(normalizedResolvedLabel)
                                  ) {
                                    isSelected = true
                                  }
                                }

                                let isCorrect = Boolean(item?.isTrue)
                                if (correctOptionLabelSet && displayLabel) {
                                  isCorrect = correctOptionLabelSet.has(displayLabel)
                                }

                                const itemClassNames = ['d-flex', 'align-items-start', 'gap-3', 'flex-wrap']
                                if (isCorrect) {
                                  itemClassNames.push('text-success', 'fw-semibold')
                                }

                                return (
                                  <li key={item?.id ?? index} className={itemClassNames.join(' ')}>
                                    <span className='text-uppercase pt-1'>{displayLabel}</span>
                                    <div className='d-flex flex-wrap align-items-center gap-2'>
                                      <div
                                        className='exam-result-html-content flex-grow-1'
                                        dangerouslySetInnerHTML={{__html: item?.name || ''}}
                                      ></div>
                                      {isCorrect && <span className='badge badge-light-success'>Doğru</span>}
                                      {isSelected && (
                                        <span className='badge badge-light-primary'>Öğrencinin Seçimi</span>
                                      )}
                                    </div>
                                  </li>
                                )
                              })}
                            </ul>
                            {correctOptionLabels.length > 0 && (
                              <div className='mt-2 text-muted'>Doğru cevap: {correctOptionLabels.join(', ')}</div>
                            )}
                            {selectedOptionLabels.length > 0 && (
                              <div className='mt-2 text-muted'>
                                Öğrencinin seçimi: {selectedOptionLabels.join(', ')}
                              </div>
                            )}
                            {question.isMultipleChoice && selectedOptionLabels.length === 0 && (
                              <div className='mt-2 text-muted fst-italic'>Öğrencinin bu soru için seçimi bulunmuyor.</div>
                            )}
                          </div>
                        )}

                        {Array.isArray(question.courseOutcomes) && question.courseOutcomes.length > 0 && (
                          <div className='mb-4'>
                            <div className='fw-semibold mb-2'>Öğrenme Çıktıları</div>
                            <ul className='mb-0 ps-4'>
                              {question.courseOutcomes.map((outcome, index) => (
                                <li key={`${question.id}-outcome-${index}`}>
                                  {(outcome.order ?? '-') + '. '} {outcome.name ?? ''}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {bookletEntries.length > 0 && (
                          <div className='mb-4'>
                            <div className='fw-semibold mb-2'>Kitapçık Sıraları</div>
                            <div className='table-responsive'>
                              <table className='table table-sm table-bordered mb-0'>
                                <thead>
                                  <tr>
                                    <th>Kitapçık</th>
                                    <th>Soru Numarası</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bookletEntries.map((entry, index) => (
                                    <tr
                                      key={`${question.id}-booklet-${index}`}
                                      className={entry.code === selectedStudentBookletCode ? 'table-active' : ''}
                                    >
                                      <td>{entry.label}</td>
                                      <td>{entry.questionNumber}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {studentAnswer?.answerText && (
                          <div className='mb-3'>
                            <div className='fw-semibold mb-2'>Öğrenci Yanıtı</div>
                            <div className='border rounded p-3 bg-light'>
                              <div
                                className='exam-result-html-content mb-0'
                                dangerouslySetInnerHTML={{__html: studentAnswer.answerText}}
                              ></div>
                            </div>
                          </div>
                        )}

                        {typeof studentAnswer?.score === 'number' && (
                          <div className='text-muted'>Sistem Kaydı: {formatNumber(studentAnswer.score)}</div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-light' onClick={closePreview}>
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show'></div>
        </>
      )}
    </div>
  )
}

export default ExamResultsInput
